import {
  Fragment, useCallback, useEffect, useMemo, useRef, useState,
  type MouseEvent as ReactMouseEvent, type ReactNode,
} from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { ActionMenu, AppToolbar } from '../../../components/AppToolbar';
import { AuditTrailButton } from '../../../components/AuditTrailButton';
import { TopbarStepper } from '../../../components/StepIsland/TopbarStepper';
import type { IslandStep } from '../../../components/StepIsland/StepIsland';
import { SectionPanel } from '../../../components/SectionPanel';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useDeleteFlow, useConfirm, useReasonGate } from '../../../components/confirm';
import { runWithAuditReason } from '../../../api/client';
import { useHotkeys, formatCombo, effectiveCombo, type HotkeyDef } from '../../../components/hotkeys';
import { resetUnsavedTracking, useUnsavedGuard } from '../../../hooks/useUnsavedGuard';
import {
  addPlmComment, addPlmLayer, addPlmLayers, clearPlmPointCount, copyPlmLayer, createPlmDataPackage,
  fetchPlmBatch, fetchPlmFiberTypeValues, fetchPlmLayer, fetchPlmLayers,
  fetchPlmLookups, fetchPlmPointCount, fetchPlmReportNames, fetchPlmSubmitCheck,
  previewPlmReport, removePlmLayer, resetPlmBatch, savePlmLayer, savePlmPointCount, savePlmSaveAudit,
  type PlmBatchHeader, type PlmExamInput, type PlmExamRow, type PlmFiberTypeValue,
  type PlmLayerRow, type PlmLookups, type PlmObservation, type PlmObservationInput,
  fetchPlmQcCompare, type PlmQcCompareRow,
  fetchPlmCorrectiveActions, savePlmCorrectiveAction, fetchPlmQcHistory, type PlmCorrectiveAction,
  addPlmReRead, submitPlmReRead, reopenPlmReRead,
  fetchPlmDailyQcStatus, overridePlmDailyQc, type PlmDailyQcStatus,
  fetchPlmCheckPoints, fetchPlmNoSelfReview, PLM_PENDING_VALIDATION, type PlmCheckPoint,
} from './plm.api';
import {
  allowedAnalyst, analysedBatch, batchTitleTooltip, blindingReRead, dailyLockReason, dupBlock, dupLabel, dupMenuHint, removeDupHint,
  claimDupHint, isUnclaimedInter, PLM_RETRIEVE_BAR_ENABLED,
  interCandidates, isBlind, dupFlowOf,
  lockOwnerOid, lockReason, originalAnalyst, ownerOf, seeable, stageTone, submitSuccessOffer, unansweredCheckPoints,
  validateBlock,
} from './plmWorkflow';
import { PlmCheckPointsTab } from './PlmCheckPointsTab';
import { usePlmValidateActions } from './usePlmValidateActions';
import { dailyAllows, dailyBar, dailyQcIdRequired, offeredDailyQcId } from './plmDailyQc';
import { fmtLocalDate, fmtLocalDateTime, nowClientWire } from '../../../utils/datetime';
import PlmDailyQcPage from './PlmDailyQcPage';
import { navigateToPage } from '../../../layouts/navigateTo';
import { useAuthStore } from '../../../auth/store';
import { balanceResult, blankExam, isBlankExam } from './plmAutofill';
import { stereoSummary, examSummary, examBalances } from './plmProgress';
import {
  pointsPerSlide, slideCount, totalPoints,
  type PlmPointCountDetail, type PlmPointCountSlide, type PointCountType,
} from './plmPointCount';
import { ComboSelect } from '../../../components/ComboSelect';
import { PlmStereoTab } from './PlmStereoTab';
import { PlmContamControl, PlmDailyQcPicker, PlmDeviceCombo, PlmDevicePick, deviceNames, prefillDevices } from './PlmDeviceFields';
import {
  fetchPlmContamination, fetchPlmDailyQcChoices, fetchPlmStationRows, fetchPlmUtilities, removePlmDup,
  EMPTY_PLM_UTILITIES, type PlmContamState, type PlmDailyQcChoice, type PlmStationRow, type PlmUtilityLists,
} from './plm.api';
import { PlmRetrieveBar } from './PlmRetrieveBar';
import { materialText } from './plmStereoOptions';
import { PlmRowMenu } from './PlmRowMenu';
import { PlmExamTab } from './PlmExamTab';
import { PlmPointCountDialog } from './PlmPointCountDialog';
import { PlmCarryOverDialog } from './PlmCarryOverDialog';
import { PlmFinalTab } from './PlmFinalTab';
import { PlmQcTab } from './PlmQcTab';
import { PAIR_LIMIT_MONTHS, toPoints, type QcPoint } from './plmQcStats';
import { PlmQcChartsPage } from './PlmQcChartsPage';
import { PlmTopbarMeta } from './PlmTopbarMeta';
import { buildQcPairs, groupLayers } from './plmQc';
import { PlmCommentDialog } from './PlmCommentDialog';
import { SdmsPackageDetail, type SdmsPackageRow } from '../SdmsPackageDetail';
import { type SdmsCheckPointEdit } from '../SdmsValidationActions';
import { qcBatchApi } from '../qcBatch.api';
import '../SdmsPackageDetail.css';
import { showDialog } from '../../../components/confirm';
import { useNavCollapse } from '../../../hooks/useNavCollapse';
import { ReturnBar } from '../../../components/ReturnToOriginBar';
import './plm.css';

/**
 * The layer lifecycle, in the prototype's own words (`PLM-Analysis-v8.html`
 * `STAGES`). ONE list, read by BOTH the docked island and the sequence rail's
 * status pill, so the two can never label the same layer differently.
 * "Approved" was dropped from the island on owner request 2026-11-03.
 */
export const PLM_STAGES = ['Pending Entry', 'Entered', 'Validated'] as const;

/**
 * Renders a layer's legacy status in that vocabulary.
 *
 * The legacy `QCBatchSequence` enum distinguishes 0 *Pending Result Entry* from
 * 1 *Pending Sample Prep*; the prototype does not, because from the analyst's
 * side both mean the same thing — the layer has not been entered — and the rail
 * is 330px wide. Anything else (Entered, and the Data-Review states) passes
 * through unchanged.
 */
export function plmStageLabel(statusText: string | null | undefined): string {
  const s = (statusText ?? '').trim();
  return /^pending\s+(result\s+entry|sample\s+prep|entry)$/i.test(s) ? 'Pending Entry' : s;
}

/**
 * PLM (Polarized Light Microscopy) Result Entry — the asbestos-bulk sibling of
 * the SDMS spreadsheet.  Opened from a QC batch whose test is an
 * "Asbestos_PLM" unconventional analysis; port of the legacy ALPACA "PLM"
 * DashboardView (BTLIMS.Web/Model.xafml :34438).
 *
 * Layout, as in legacy: a narrow LAYER list on the left (one row per sample
 * layer, green once entered), a sample-context strip across the top, and two
 * tabs — Stereoscopic Observation and PLM Exam.
 *
 * All six phases are in: the core entry loop, the 400/1000-point count, the
 * Copy / Carry-Over family, Reset / Delete / Comment, Report Preview, and the
 * Submit → Data Package hand-off.  PLM Quality Control is not scheduled — its
 * table has no rows in any database on this server.
 */

const AUDIT_FORM = 'PLM';
/** The most layers one Add Layer may add (a sample's layers are lettered A to Z). The API checks the same. */
const MAX_LAYERS_AT_ONCE = 25;

/** G5's words when Report Preview is refused for blind analysis (PlmV2WorkspaceController.Preview_Execute). */
export const PLM_REPORT_BLIND_TEXT = '🔒 Blind analysis — this batch has a sample with an Inter-dup that is hidden from you, '
  + 'so the report is available to its original analyst, or to everyone once the batch is submitted.';

export interface PlmResultEntryPageProps {
  /** The QC batch being worked. */
  batchOid: string;
  /** Return to whatever opened this screen (the QC batch detail, or its list). */
  onBack: () => void;
  /** After a Validate or Reject (owner 2026-10-19, G5 OpenEmpty(Validate)): PLM stays on its Validate list
   *  instead of going Back to whatever opened the batch (e.g. the QC Batch form). Back when omitted. */
  onValidateDone?: () => void;
  /** v13 mode (2026-09-28): 'view' opens the batch READ-ONLY (from the View
   *  list); 'enter' (default, also the QC Batch form's path) is editable;
   *  'validate' (2026-09-29) is read-only with Validate / Reject and the
   *  package's Check Points. */
  mode?: 'view' | 'enter' | 'validate';
  /** Open on this layer — e.g. a re-read just started from "+ New QC". */
  initialSequenceOid?: string;
  /** The v13 View · Enter · Validate + QC switch, docked in the top bar right
   *  after the title (PlmPage supplies it; the QC Batch form's path has none). */
  modeSwitch?: ReactNode;
  /** Switch this batch to Enter mode — called after a rollback run from View
   *  mode, so the analyst can carry on with the reopened batch. */
  onEnterMode?: () => void;
  /** The Retrieve bar (G5 parity 2026-10-16): open another batch, in the chosen
   *  mode, without going back to the list. PlmPage supplies it (after the shared
   *  unsaved-changes confirm); without it the bar is not shown. */
  onRetrieve?: (batchOid: string, mode: 'view' | 'enter' | 'validate') => void;
  /** Open THIS batch in Validate mode — the "Go to Validation" button on the success dialog after
   *  Submit (owner 2026-11-03). Without it the dialog offers only OK. */
  onGoToValidation?: () => void;
}

/** Strips the read-only halves off an observation to get the editable draft. */
function toDraft(o: PlmObservation): PlmObservationInput {
  const {
    sequenceOid: _s, observationOid: _o, sampleL: _l, sampleNo: _n, clientSampleId: _c,
    sampleLocation: _loc, status: _st, materialNames: _mn, sampleNote: _sn,
    analyzedByName: _ab, enteredByOid: _eo, enteredByName: _en, enteredDate: _ed,
    dispersionOilName: _dn, receivedDate: _rd,
    dailyQcOid: _dq, contaminationCheckedByName: _cb, contaminationCheckedDate: _cd,
    ...draft
  } = o;
  return draft;
}

function toExamInput(e: PlmExamRow): PlmExamInput {
  const { fiberType: _ft, fiberColor: _fc, ...rest } = e;
  return rest;
}

export default function PlmResultEntryPage({
  batchOid, onBack, onValidateDone, mode = 'enter', initialSequenceOid, modeSwitch, onEnterMode, onRetrieve,
  onGoToValidation,
}: PlmResultEntryPageProps) {
  const notify = useNotify();
  // The signed-in user's Employee Oid — the v13 ownership / blind rules key on it.
  const me = useAuthStore(s => s.user?.id ?? null);
  // View AND Validate are read-only; Validate adds Validate / Reject + Check Points.
  const viewMode = mode !== 'enter';
  const validateMode = mode === 'validate';
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const gateReason = useReasonGate();
  const { confirm, prompt } = useConfirm();

  const [header, setHeader]   = useState<PlmBatchHeader | null>(null);
  const [layers, setLayers]   = useState<PlmLayerRow[]>([]);
  const [lookups, setLookups] = useState<PlmLookups>({
    fiberTypes: [], componentNames: [], fiberColors: [],
    materials: [], sampleNotes: [], analysts: [], dispersionOils: [],
  });
  const [fiberTypeValues, setFiberTypeValues] = useState<PlmFiberTypeValue[]>([]);

  const [activeSeq, setActiveSeq] = useState<string | null>(null);
  const [context, setContext]     = useState<PlmObservation | null>(null);
  const [draft, setDraft]         = useState<PlmObservationInput | null>(null);
  const [exams, setExams]         = useState<PlmExamInput[]>([]);
  const [examIndex, setExamIndex] = useState(0);
  const [tab, setTab]             = useState<'stereo' | 'exam' | 'qc' | 'final' | 'checkpoints'>('stereo');
  // Validate mode: the data package's check points + the validator's unsaved
  // answers (saved just before Validate), and the lab's "No Self-Review" switch.
  const [checkPoints, setCheckPoints] = useState<PlmCheckPoint[]>([]);
  const [cpLoading, setCpLoading] = useState(false);
  const [cpEdits, setCpEdits] = useState<Map<string, SdmsCheckPointEdit>>(new Map());
  const [noSelfReview, setNoSelfReview] = useState(false);
  const { validate: runValidate, reject: runReject } = usePlmValidateActions();
  useEffect(() => {
    if (!validateMode) return;
    let alive = true;
    setCpLoading(true);
    fetchPlmCheckPoints(batchOid)
      .then(rows => { if (alive) setCheckPoints(rows); })
      .catch(() => { if (alive) setCheckPoints([]); })
      .finally(() => { if (alive) setCpLoading(false); });
    void fetchPlmNoSelfReview().then(v => { if (alive) setNoSelfReview(v); });
    return () => { alive = false; };
  }, [validateMode, batchOid]);
  // QC Result View data — batch-wide, so it is fetched once per batch (and after
  // a save, which can change what a pair compares) rather than per layer.
  const [qcRows, setQcRows]       = useState<PlmQcCompareRow[]>([]);
  // Prototype v18 QC Result View: the corrective actions on failed pairs, and
  // the lab's re-reads of the last 3 months behind each pair's limit line
  // (null until the QC tab is first opened — it is not needed before).
  const [corrective, setCorrective] = useState<PlmCorrectiveAction[]>([]);
  const [qcHistory, setQcHistory]   = useState<QcPoint[] | null>(null);
  const [qcLoading, setQcLoading] = useState(false);

  const [loading, setLoading]   = useState(true);
  const [loadMs, setLoadMs]     = useState<number | null>(null);
  const [dirty, setDirty]       = useState(false);
  const [busy, setBusy]         = useState(false);

  // Point count (Phase 2). Unlike the observation draft this is NOT part of the
  // layer save: the PC Entry dialog commits on its own OK, because the count
  // rewrites the exam Results server-side and the grid must reload to show them.
  const [pointCount, setPointCount] = useState<PlmPointCountDetail>({
    pointCountType: null, fiberNames: [], slides: [],
  });
  const [pcOpen, setPcOpen] = useState(false);

  // Carry Over Results (Phase 3) — copy one layer onto others.
  const [carryOpen, setCarryOpen] = useState(false);

  // Comment (Phase 4) — a note filed against the batch's job(s).
  const [commentOpen, setCommentOpen] = useState(false);

  // Data Package (Phase 6) — Submit opens the SHARED package popup; the batch is
  // advanced by the Submit INSIDE it, not by this screen. See doSubmit.
  const [pkgOpen, setPkgOpen]       = useState(false);
  const [pkgStatus, setPkgStatus]   = useState<number | null>(null);
  const [pkgCreated, setPkgCreated] = useState<{ by: string | null; date: string | null }>({ by: null, date: null });

  // v13 Daily QC (2026-09-28): today's state for the signed-in analyst, and the
  // Daily QC screen opened from the bar above the sequence (null = closed;
  // oid null = start on the slide picker).
  const [dailyStatus, setDailyStatus] = useState<PlmDailyQcStatus | null>(null);
  // G5 parity (2026-10-15): the PLM Instrument Setup rows behind the four device
  // drop-downs, the PASSED Daily QC reads behind the Daily QC ID picker, and the
  // open layer's contamination badge.
  const [stationRows, setStationRows] = useState<PlmStationRow[]>([]);
  // The remembered device and RI Oil names (dbo.AI_PlmUtility, G5 parity 2026-10-16).
  const [utilities, setUtilities] = useState<PlmUtilityLists>(EMPTY_PLM_UTILITIES);
  const [dailyChoices, setDailyChoices] = useState<PlmDailyQcChoice[]>([]);
  const [contam, setContam] = useState<PlmContamState | null>(null);
  const [dailyOpen, setDailyOpen] = useState<{ oid: string | null } | null>(null);

  // Report preview (Phase 5) — the lab's configured PLM reports.
  const [reportNames, setReportNames] = useState<string[]>([]);
  const [reportName, setReportName]   = useState('');

  // The layer grid + the two tabs hold state the generic edit-tracker cannot
  // see (a combo pick, a checkbox inside a custom group), so the page declares
  // its own dirty flag — standing rule 15.

  // The BATCH is past result entry (submitted). Batch-level actions — Submit,
  // Reset, Rollback — key on this. The per-layer `readOnly` (below, once the
  // active layer is known) adds View mode and the v13 ownership / blind locks.
  const batchLocked = !(header?.editable ?? false);
  /** The QC dup flow (AI_AppSettings 'PlmDupSubmitButtons', owner decision 2026-10-17):
   *  'On' = today's Start / Submit Read / Re-open Read; otherwise the SIMPLE flow — dups
   *  are entered like sample layers, an Inter-dup is any analyst's but the original
   *  analyst's (the first to save it owns it), and while the batch is open the original
   *  and Intra-dup of a group with an Inter-dup are blind to all but the original analyst. */
  const dupFlow = useMemo(() => dupFlowOf(lookups.dupSubmitButtons, !batchLocked),
    [lookups.dupSubmitButtons, batchLocked]);
  /** The separate dup steps are shown ('On'). */
  const dupSteps = !dupFlow.simple;
  const dupFlowRef = useRef(dupFlow);
  dupFlowRef.current = dupFlow;

  /** Options for the Analyzed By combo.
   *
   *  The server list is legacy's: lab users + LIMS administrators of the current
   *  user's company, plus the current user (AI_Plm_Lookups_SP result set 6). A
   *  layer CAN name someone outside that — an admin who moved company, say — and
   *  in legacy the XAF lookup still DISPLAYS a stored object outside its
   *  datasource criteria; it simply is not re-selectable. ComboSelect instead
   *  renders a value it cannot find as EMPTY (ComboSelect.tsx :58, no
   *  allowCustom), so the layer would show a blank analyst on a record that has
   *  one — reading as data loss, and inviting a re-pick that overwrites the real
   *  analyst. Appending the record's own analyst reproduces legacy exactly while
   *  keeping the offered list at legacy's own membership. */
  const analystOptions = useMemo(() => {
    // Owner rule 2026-09-29: a re-read offers only the analysts it may be
    // performed by — an Intra dup the original layer's analyst, an Inter dup
    // nobody who analysed the batch (PlmLayerRules.AnalystBlock refuses the rest).
    const current = layers.find(l => l.sequenceOid === activeSeq) ?? null;
    const opts = lookups.analysts
      .filter(a => allowedAnalyst(current, layers, a.id, dupFlow))
      .map(a => ({ value: a.id, label: a.name ?? '' }));
    const stored = draft?.analyzedByOid;
    if (stored && !opts.some(o => o.value === stored)) {
      opts.push({ value: stored, label: context?.analyzedByName ?? '(no longer listed)' });
    }
    return opts;
  }, [lookups.analysts, layers, activeSeq, draft?.analyzedByOid, context?.analyzedByName, dupFlow]);


  // ── load ───────────────────────────────────────────────────────────────────

  const loadShell = useCallback(async () => {
    const t0 = performance.now();
    setLoading(true);
    try {
      // QC comparison rides along with the shell so the tab's caption is live
      // and every `loadShell()` call site (Refresh, post-rollback) refreshes the
      // QC verdicts too.  The layer writes do NOT go through here, so they call
      // refreshQc() themselves (below).
      setQcLoading(true);
      // The Daily QC state loads ALONGSIDE, not inside the Promise.all: if that
      // one call fails the screen must still open (the server refuses a locked
      // write anyway — 403 daily-qc-required).
      void fetchPlmDailyQcStatus().then(setDailyStatus);
      void fetchPlmStationRows().then(setStationRows).catch(() => setStationRows([]));
      void fetchPlmUtilities().then(setUtilities).catch(() => setUtilities(EMPTY_PLM_UTILITIES));
      void fetchPlmDailyQcChoices().then(setDailyChoices).catch(() => setDailyChoices([]));
      const [h, rows, lk, ftv, names, qc] = await Promise.all([
        fetchPlmBatch(batchOid),
        fetchPlmLayers(batchOid),
        fetchPlmLookups(),
        fetchPlmFiberTypeValues(),
        fetchPlmReportNames(),
        fetchPlmQcCompare(batchOid),
      ]);
      setQcRows(qc);
      void fetchPlmCorrectiveActions(batchOid).then(setCorrective).catch(() => setCorrective([]));
      setHeader(h);
      setLayers(rows);
      setLookups(lk);
      setFiberTypeValues(ftv);
      setReportNames(names);
      setReportName(prev => prev || names[0] || '');
      // Open on the requested layer (a re-read just started), else the first
      // layer this user may see (v13: another analyst's pending Inter re-read
      // is not listed).
      setActiveSeq(prev => prev
        ?? (initialSequenceOid && rows.some(r => r.sequenceOid === initialSequenceOid) ? initialSequenceOid : null)
        ?? rows.find(r => seeable(r, me, rows, dupFlowOf(lk.dupSubmitButtons, !!h?.editable)))?.sequenceOid ?? null);
    } finally {
      setLoading(false);
      setQcLoading(false);
      setLoadMs(performance.now() - t0);
    }
  }, [batchOid, initialSequenceOid, me]);

  useEffect(() => { void loadShell(); }, [loadShell]);

  /**
   * Re-reads the QC comparison.  A saved layer can be either side of a re-read
   * pair, so its verdict moves with every layer write.  Until 2026-09-26 only
   * loadShell read it, so after saving both re-reads of a real batch the QC
   * Result View still said "2 pending · not entered" until the page was reopened.
   */
  const refreshQc = useCallback(async () => {
    setQcRows(await fetchPlmQcCompare(batchOid));
  }, [batchOid]);

  /** Saves a failed pair's corrective action (prototype v18), then re-reads them. */
  const saveCorrective = useCallback(async (sequenceOid: string, text: string) => {
    await savePlmCorrectiveAction(sequenceOid, text);
    setCorrective(await fetchPlmCorrectiveActions(batchOid));
    notify.success(text ? 'Corrective action saved.' : 'Corrective action removed.');
  }, [batchOid, notify]);

  // The limit line's reference set — the lab's re-reads of the rolling 3 months,
  // read once, the first time the QC Result View is opened.
  useEffect(() => {
    if (tab !== 'qc' || qcHistory !== null) return;
    const since = new Date();
    since.setMonth(since.getMonth() - PAIR_LIMIT_MONTHS);
    const from = `${since.getFullYear()}-${String(since.getMonth() + 1).padStart(2, '0')}-${String(since.getDate()).padStart(2, '0')}`;
    void fetchPlmQcHistory(from).then(rows => setQcHistory(toPoints(rows))).catch(() => setQcHistory([]));
  }, [tab, qcHistory]);

  // Read through refs so loadLayer keeps its stable identity.
  const ruleRef = useRef({ layers, me, viewMode });
  ruleRef.current = { layers, me, viewMode };

  /** A re-read nobody has started yet gets the analyst the owner rule
   *  (2026-09-29) implies — an Intra dup its original analyst, an Inter dup the
   *  signed-in user when they may take it — so the required field is not left
   *  for the user to discover. Loading it is not an edit (dirty stays false). */
  const withReReadAnalyst = useCallback((d: PlmObservationInput, sequenceOid: string): PlmObservationInput => {
    if (d.analyzedByOid) return d;
    const { layers: all, me: user } = ruleRef.current;
    const l = all.find(x => x.sequenceOid === sequenceOid);
    if (l?.reReadKind === 'Intra') return { ...d, analyzedByOid: originalAnalyst(l, all) };
    if (l?.reReadKind === 'Inter' && user && !analysedBatch(all, user)) return { ...d, analyzedByOid: user };
    return d;
  }, []);

  // The Daily QC ID the server offers per layer (GET layers/{id} layerDailyQcId, G5 parity).
  const layerDaily = useRef(new Map<string, string | null>());
  // The layers already offered a Daily QC ID since they last loaded (see the effect below).
  const dailyOffered = useRef(new Set<string>());

  const loadLayer = useCallback(async (sequenceOid: string) => {
    // Parked edits win over the server copy — otherwise switching back to a layer
    // would silently revert the analyst's unsaved work.
    const parked = pending.current.get(sequenceOid);
    if (parked) {
      // Its own header comes back with it: Entered Date / By and the names the
      // header shows are read from `context`, which would otherwise still be
      // the layer the analyst just left.
      if (parked.context) setContext(parked.context);
      setDraft(parked.draft);
      setExams(parked.exams);
      setDirty(true);
      return;
    }
    const [data, pc] = await Promise.all([
      fetchPlmLayer(sequenceOid),
      fetchPlmPointCount(sequenceOid),
    ]);
    if (!data) return;
    layerDaily.current.set(sequenceOid, data.layerDailyQcId ?? null);
    // A suggested ID is not stored, so this fresh copy has lost it: offer it again.
    // A parked layer returns above and keeps whatever the analyst left in the box.
    dailyOffered.current.delete(sequenceOid);
    setContext(data.observation);
    setContam(data.contamination ?? null);
    const opened = withReReadAnalyst(toDraft(data.observation), sequenceOid);
    // The PLM Scope is entered once per batch (user feedback C7, 2026-11-02): an offer, not an
    // edit, so dirty stays false below. Parked layers count, sample layers only, as the server's
    // own answer does; View and Validate never change what they show.
    const { layers: rows, viewMode: viewing } = ruleRef.current;
    const unsaved = [...pending.current.entries()]
      .filter(([seq]) => !rows.find(l => l.sequenceOid === seq)?.reReadKind)
      .map(([, parkedLayer]) => parkedLayer.draft);
    setDraft(viewing ? opened : prefillDevices(opened, data.deviceDefaults, unsaved));
    // Legacy's PLM Exam grid always shows ONE empty row, ready to type into
    // (user request 2026-09-23).  Seeded HERE, at the point the layer's exams
    // arrive — an earlier attempt seeded from its own effect and lost the race:
    // the effect ran on activeSeq changing, then this async load replaced the
    // blank row with the server's empty list.
    //
    // `setDirty(false)` below keeps this from arming the unsaved-changes guard,
    // and isBlankExam drops the row at save if it was never filled.
    const loaded = data.exams.map(toExamInput);
    setExams(loaded.length === 0 ? [blankExam(1)] : loaded);
    setPointCount(pc);
    setExamIndex(0);
    setDirty(false);
  }, [withReReadAnalyst]);

  // The contamination badge follows the station (set by a device pick) and the
  // box while the layer is edited.
  const contamStation = draft?.station ?? null;
  const contamChecked = draft?.contaminationCheck ?? false;
  useEffect(() => {
    if (!activeSeq) return;
    let alive = true;
    fetchPlmContamination(activeSeq, contamStation, contamChecked)
      .then(s => { if (alive) setContam(s); })
      .catch(() => { /* the badge is advisory; the save enforces the rule */ });
    return () => { alive = false; };
  }, [activeSeq, contamStation, contamChecked]);

  // The layer list, read through a ref so a list refresh does not re-run the
  // load below (that would overwrite the layer on screen, unsaved edits and all).
  const layersRef = useRef(layers);
  layersRef.current = layers;

  // When each layer was first opened in this session. A layer still pending
  // entry shows it as its Analyzed Date until the first save stamps the real
  // one (owner request 2026-10-07 / 10-08): filled as soon as the layer opens,
  // and steady while the analyst moves between samples rather than jumping to
  // "now" on every switch.
  const openedAt = useRef(new Map<string, string>());

  useEffect(() => {
    if (!activeSeq) return;
    if (!openedAt.current.has(activeSeq)) openedAt.current.set(activeSeq, nowClientWire());
    // v13 blind rule: a layer withheld from this user is not fetched at all — the
    // server refuses it too (403 layer-blind); the screen shows the blind card.
    const l = layersRef.current.find(x => x.sequenceOid === activeSeq);
    if (l && isBlind(l, layersRef.current, me, dupFlowRef.current)) {
      // Nothing of the previous layer may linger under this one's name — and a
      // later switch must not park it against this layer. The previous layer's
      // own edits were already parked by the switch (stashActive).
      setContext(null);
      setDraft(null);
      setExams([]);
      setDirty(false);
      return;
    }
    void loadLayer(activeSeq);
  }, [activeSeq, loadLayer, me]);

  // ── unsaved edits survive a layer switch ──────────────────────────────────
  //
  // Legacy keeps every edited layer in ONE XAF ObjectSpace: the analyst works
  // down the sample list, then Saves once, and switching rows never discards
  // anything (user report 2026-09-23 — AI was prompting "Unsaved changes will be
  // lost" and throwing the work away).
  //
  // Mirrored with a stash keyed by sequence Oid: switching AWAY parks the dirty
  // layer here, switching BACK restores it instead of re-reading the server, and
  // Save persists the active layer plus everything parked.  A ref, not state, so
  // stashing inside the switch handler cannot trigger a re-render mid-swap.
  const pending = useRef(new Map<string, {
    draft: PlmObservationInput; exams: PlmExamInput[]; context?: PlmObservation | null;
  }>());
  // The parked layers, mirrored in state so the screen follows them (G5 parity
  // 2026-10-16): Save stays on, the leave guard holds and the note counts them
  // while ANY layer is unsaved — not only the one on screen.
  const [parkedKeys, setParkedKeys] = useState<string[]>([]);
  const syncParked = useCallback(() => setParkedKeys([...pending.current.keys()]), []);
  const clearParked = useCallback(() => { pending.current.clear(); setParkedKeys([]); }, []);
  // Leaving the form warns while ANY layer is unsaved, parked ones included (G5 parity).
  useUnsavedGuard(dirty || parkedKeys.length > 0);

  /** Park the layer currently on screen, if the analyst changed it. */
  const stashActive = useCallback(() => {
    if (dirty && activeSeq && draft) pending.current.set(activeSeq, { draft, exams, context });
    syncParked();
  }, [dirty, activeSeq, draft, exams, context, syncParked]);

  // ── layer switching (guarded) ──────────────────────────────────────────────

  const selectLayer = useCallback(async (sequenceOid: string) => {
    if (sequenceOid === activeSeq) return;
    // No "unsaved changes" prompt — the edits are kept, not discarded (legacy
    // parity).  The guard that DOES still apply is leaving the whole form, which
    // useUnsavedGuard handles below against `dirty`.
    stashActive();
    // G5: another layer always opens on its first tab (Stereoscopic Observation).
    setTab('stereo');
    setActiveSeq(sequenceOid);
  }, [activeSeq, stashActive]);

  // ── mutations ──────────────────────────────────────────────────────────────

  /** Anything on the page not yet saved: the layer on screen, or a layer parked
   *  by a switch.  `dirty` alone covers only the on-screen one. */
  const hasUnsavedEdits = useCallback(
    () => dirty || pending.current.size > 0, [dirty]);
  /** How many layers hold unsaved work: the parked ones and the one on screen. */
  const unsavedCount = new Set([...parkedKeys, ...(dirty && activeSeq ? [activeSeq] : [])]).size;
  const anyUnsaved = unsavedCount > 0;

  /**
   * Writes every edited layer — the one on screen and any parked by a switch.
   * Runs INSIDE a save flow (the caller supplies the confirm, reason and loader).
   *
   * Shared by Save and by the point-count writes.  Those reload the layer when
   * they finish, and a reload over unsaved work throws it away without a word —
   * which is exactly what happened on 2026-09-26: PC Entry → Apply on a freshly
   * added layer saved the count, reloaded, and the analyst's header, stereo and
   * exam entries vanished (standing rule 15).  So they save the work FIRST.
   */
  const persistEdits = useCallback(async () => {
    if (!activeSeq || !draft) return;
    // EVERY edited layer is persisted, not just the one on screen — legacy
    // saves the whole ObjectSpace, and layer switching now KEEPS edits, so
    // saving only the active layer would silently drop the rest.
    //
    // The tab opens with one blank row ready to type into (legacy parity);
    // drop it again if it was never filled, so an empty component is not
    // saved just because the analyst visited the tab.
    const clean = (rows: PlmExamInput[]) => rows.filter(e => !isBlankExam(e));
    // The Material Description is stored as typed, less the surrounding blanks
    // (it is edited untrimmed, so a space mid-sentence is not eaten as you type).
    const tidy = (o: PlmObservationInput): PlmObservationInput =>
      ({ ...o, materialIds: o.materialIds?.trim() || null });

    // A refusal names its layer (G5: "260929010-001.B: Add at least one component…"):
    // one Save writes several layers, so the message must say which one failed.
    const named = async (seq: string, write: () => Promise<unknown>) => {
      try { await write(); }
      catch (e) {
        const err = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error;
        const code = layersRef.current.find(x => x.sequenceOid === seq)?.sampleL;
        if (err?.message && code && !err.message.startsWith(code)) err.message = `${code}: ${err.message}`;
        throw e;
      }
    };
    // One Save, one Analyzed Date (owner 2026-10-17, G5 parity: SaveAll's savedAt): the
    // first layer's answer is sent back on every other layer of this Save.
    let savedAt: string | null = null;
    const saved: string[] = [];
    const write = async (seq: string, input: { observation: PlmObservationInput; exams: PlmExamInput[] }) => {
      await named(seq, async () => {
        const res = await savePlmLayer(seq, { ...input, savedAt });
        savedAt ??= res.savedAt ?? null;
      });
      const code = layersRef.current.find(x => x.sequenceOid === seq)?.sampleL;
      if (code && !saved.includes(code)) saved.push(code);
    };
    for (const [seqOid, parked] of pending.current) {
      if (seqOid === activeSeq && dirty) continue;   // the on-screen copy is newer
      await write(seqOid, { observation: tidy(parked.draft), exams: clean(parked.exams) });
    }
    // The layer on screen is written only when it was changed: Save can now be
    // pressed from a clean layer to write the parked ones (G5 parity).
    if (dirty) await write(activeSeq, { observation: tidy(draft), exams: clean(exams) });
    return saved;
  }, [activeSeq, draft, exams, dirty]);

  const doSave = useCallback(async () => {
    if (!activeSeq || !draft) return;

    // CROSS-TAB CHECK (prototype v8). The two tabs can contradict each other:
    // the stereo tab can say "No Asbestos Detected" while the PLM Exam lists an
    // Asbestos Fibers component, or the exam can list one with NAD unticked.
    // Legacy checked neither, so the contradiction reached the report.
    //
    // WARNING, not a block: which one is right is the analyst's call — they may
    // be mid-entry, or the component may be there deliberately. So it is raised
    // at save, names both sides, and is confirmed rather than refused.
    const asbestosTypeId = lookups.fiberTypes.find(t => t.name === 'Asbestos Fibers')?.id;
    const namedAsbestos = asbestosTypeId
      ? exams.filter(e => e.fiberTypeOid === asbestosTypeId && (e.name ?? '').trim())
      : [];
    if (dirty && draft.noAsbestosDetected && namedAsbestos.length > 0) {
      const ok = await confirm({
        title: 'Check this layer',
        message: 'The Stereoscopic tab says No Asbestos Detected, but the PLM Exam '
               + `lists ${namedAsbestos.length === 1 ? 'an asbestos component' : 'asbestos components'} `
               + `(${namedAsbestos.map(e => e.name).join(', ')}). Save anyway?`,
      });
      if (!ok) return;
    }

    // The reason for change (when Settings → Audit Trail Configuration requires one for PLM)
    // goes on ONE batch-level "Save" row naming the layers, not on each field row (owner
    // 2026-10-17, G5 parity: Save_Execute) — so the layer saves are sent without it.
    const reason = await gateReason(AUDIT_FORM, 'edit');
    if (reason === null) return;
    await runSave({
      save: async () => {
        const saved = await persistEdits();
        if (reason && saved && saved.length > 0) {
          await runWithAuditReason(reason, () => savePlmSaveAudit(batchOid, saved.join(', ')));
        }
      },
      onSaved: async () => {
        clearParked();
        setDirty(false);
        // The save can flip this layer to "Entered" and the batch to Status 1,
        // so re-read both the list and the layer rather than patching locally.
        const [rows] = await Promise.all([
          fetchPlmLayers(batchOid), loadLayer(activeSeq), refreshQc(),
        ]);
        setLayers(rows);
        const h = await fetchPlmBatch(batchOid);
        setHeader(h);
        // A name typed into a device box or RI Oil is now remembered (G5 parity).
        void fetchPlmUtilities().then(setUtilities).catch(() => undefined);
      },
    });
  }, [activeSeq, draft, exams, runSave, batchOid, loadLayer, confirm, lookups.fiberTypes, persistEdits, refreshQc, clearParked, gateReason]);

  // How many layers Add Layer adds, from the box beside it (user feedback C6, 2026-11-02).
  const [addLayerCount, setAddLayerCount] = useState('1');

  const doAddLayer = useCallback(async () => {
    if (!activeSeq) return;
    const count = Number(addLayerCount);
    if (!Number.isInteger(count) || count < 1 || count > MAX_LAYERS_AT_ONCE) {
      notify.warning(`Enter how many layers to add, from 1 to ${MAX_LAYERS_AT_ONCE}.`);
      return;
    }
    const ok = await confirm({
      title: 'Add Layer',
      message: count === 1 ? 'Add the next layer for this sample?' : `Add ${count} layers to this sample?`,
    });
    if (!ok) return;
    setBusy(true);
    try {
      if (count === 1) {
        const added = await addPlmLayer(activeSeq);
        setLayers(await fetchPlmLayers(batchOid));
        notify.success(`Layer ${added.sampleL ?? ''} added successfully.`);
      } else {
        const res = await addPlmLayers(activeSeq, count);
        setLayers(await fetchPlmLayers(batchOid));
        if (res.stoppedBy) notify.warning(`Added ${res.added.length} of ${count} layers. Check the Sequence list and try again.`);
        else notify.success(`${count} layers added successfully.`);
      }
      setAddLayerCount('1');
      // STAY on the layer the analyst is working on. Legacy's
      // PLMAddLayer_Execute (:1485) creates the sequence, adds it to the
      // collection and calls View.Refresh() — it never assigns
      // View.CurrentObject — so the new layer appears in the rail and the
      // analyst keeps their place. Jumping to it (the behaviour until
      // 2026-09-24) also risked their in-progress edits: a layer switch parks
      // the draft, so adding a layer mid-entry moved them off work they had
      // not finished.
    } finally {
      setBusy(false);
    }
  }, [activeSeq, addLayerCount, batchOid, confirm, notify]);

  const doRemoveLayer = useCallback(async (target?: PlmLayerRow) => {
    const seq = target?.sequenceOid ?? activeSeq;
    if (!seq) return;
    const row = layers.find(l => l.sequenceOid === seq);
    // QC dups are optional (Bin Yu 2026-10-15): an Intra-dup / Inter-dup row is
    // removed with its read, until the read or the batch is submitted.
    const dup = !!row?.reReadKind;
    // Prototype v18: say when results would go with it. A reason only when Settings →
    // Audit Trail Configuration requires one for PLM (owner 2026-10-17, G5 parity:
    // PlmV2Ui.WithReason) — the shared delete flow's gate.
    const entered = !!row && (row.hasMaterial || row.examCount > 0);
    await runDelete({
      formName: AUDIT_FORM,
      title: dup ? 'Remove QC dup' : 'Remove Layer',
      message: (dup
        ? `Remove the QC dup ${row?.sampleL ?? ''} from the sequence?`
        : `Remove layer ${row?.sampleL ?? ''} and everything recorded against it?`)
        + (entered ? ' Results already entered on it will be discarded.' : '')
        + ' The removal is written to the audit trail.',
      confirmLabel: 'Remove',
      successMessage: dup ? 'QC dup removed successfully.' : 'Layer removed successfully.',
      delete: async () => { if (dup) await removePlmDup(seq); else await removePlmLayer(seq); },
      onDeleted: async () => {
        const rows = await fetchPlmLayers(batchOid);
        setLayers(rows);
        // A dup removed from the right-click menu leaves the open layer where it is.
        if (seq === activeSeq || !rows.some(r => r.sequenceOid === activeSeq)) {
          setActiveSeq(rows[0]?.sequenceOid ?? null);
          setDirty(false);
        }
      },
    });
  }, [activeSeq, layers, runDelete, batchOid]);

  // ── submit → Data Package (Phase 6) ────────────────────────────────────────

  /** The success dialog after the package's Submit (owner 2026-11-03): G5's "Samples submitted
   *  successfully." plus, when this user may validate the batch now, a "Go to Validation" button that
   *  opens it in Validate mode. The status is re-read AFTER the submit, because the lab's review chain
   *  decides where it went; if it cannot be read, only OK is offered. */
  const afterSubmit = useCallback(async (advanced: boolean) => {
    let status: number | null = null;
    if (advanced && onGoToValidation) {
      try {
        status = (await fetchPlmBatch(batchOid))?.status ?? null;
      } catch {
        status = null;   // no button, the message still shows
      }
    }
    const offer = submitSuccessOffer(advanced && !!onGoToValidation, status);
    const picked = await showDialog({
      type: 'success', title: 'Submitted', message: offer.message,
      actions: offer.canValidate
        ? [{ label: 'OK', value: 'ok' }, { label: 'Go to Validation', primary: true, value: 'validate' }]
        : undefined,
    });
    if (picked === 'validate') onGoToValidation?.();
  }, [batchOid, onGoToValidation]);

  /** Legacy's Complete: check the three gates, then OPEN the Data Package.
   *
   *  Every blocker is listed at once — legacy stops at the first and makes the
   *  analyst fix, retry, and discover the next — and each one names the layers
   *  at fault. The wording of each is legacy's, verbatim.
   *
   *  This does NOT advance the batch, and deliberately says nothing about having
   *  submitted it. Legacy's Complete
   *  (QCBatchController.AddDataPackage_Execute :13862) find-or-creates the
   *  DataPackage and opens DataPackage_DetailView as a modal; the batch moves
   *  Pending Submission → Pending Validation only when the analyst answers the
   *  check points and presses Submit INSIDE that popup (QBIDSubmit) — wired here
   *  through `onSubmitted`. An earlier version stopped at the create and reported
   *  "Submitted for review.", so the QC Batch row stayed on Pending Submission
   *  while the screen claimed otherwise (user report 2026-09-24). Same shape as
   *  SdmsResultEntryPage.doSubmit, which is the other caller of this flow. */
  const doSubmit = useCallback(async () => {
    if (!activeSeq && layers.length === 0) return;

    setBusy(true);
    try {
      // A dirty layer must be saved first: the gates read what is PERSISTED,
      // so an unsaved analyst/date would read as missing.
      if (dirty) {
        await showDialog({
          type: 'warning',
          title: 'Unsaved changes',
          message: 'Save this layer before submitting the batch — the checks read saved data.',
        });
        return;
      }

      let check;
      try {
        check = await fetchPlmSubmitCheck(batchOid);
      } catch {
        // Without this the failure reached `void doSubmit()` and was discarded,
        // so a 500 on the gate check looked exactly like a dead button (user
        // report 2026-09-24). A check that cannot RUN must say so rather than
        // silently refusing to submit.
        await showDialog({
          type: 'error',
          title: 'Cannot submit yet',
          message: 'The pre-submit checks could not be run. Try Refresh, and if it keeps happening report it.',
        });
        return;
      }

      if (!check.canSubmit) {
        await showDialog({
          type: 'error',
          title: 'Cannot submit yet',
          // Short (owner 2026-10-15): "Cannot submit: Enter all sample results." - one
          // sentence per kind of problem, no layer lists. A blocked batch with no listed
          // reason would otherwise open an EMPTY dialog.
          message: check.blockers.length === 0
            ? 'This batch is not ready to submit yet.'
            : ['Cannot submit:', ...check.blockers.map(b => b.message)].join(' '),
        });
        return;
      }
    } finally {
      setBusy(false);
    }

    // Find-or-create the package (its own server-side gates run here), then open it.
    setBusy(true);
    try {
      const pkg = await createPlmDataPackage(batchOid);
      // Legacy asks the QC-fail confirm BEFORE opening (:8343). Cancelling opens
      // nothing — and submits nothing, since the advance lives inside the popup.
      if ((pkg.qcFailCount ?? 0) > 0) {
        const go = await confirm({
          title: 'Submit', tone: 'danger',
          message: 'QC failed. Are you sure you want to submit?',
        });
        if (!go) return;
      }
      setPkgStatus(pkg.status);
      setPkgCreated({ by: pkg.batchCreatedBy, date: pkg.packageCreatedDate });
      setPkgOpen(true);
    } catch (e) {
      await showDialog({
        type: 'error',
        title: 'Cannot submit yet',
        message: (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Could not open the Data Package.',
      });
    } finally {
      setBusy(false);
    }
  }, [activeSeq, layers.length, dirty, batchOid, confirm]);

  // ── report preview (Phase 5) ───────────────────────────────────────────────

  /** Renders the chosen report and opens it in a new tab — legacy's
   *  window.open of the exported PDF. A dirty form is saved first, because the
   *  report reads what is PERSISTED and would otherwise omit the analyst's
   *  last edits without saying so. */
  /** The Statistical QC charts swap in over the form, like the Data Package
   *  popup does — they are about the whole lab's QC, not this layer, so they
   *  are not a tab on a layer's form. */
  const [chartsOpen, setChartsOpen] = useState(false);
  // The chart the QC Result View's "open chart →" asks for (Inter by default).
  const [chartsType, setChartsType] = useState<'Inter' | 'Intra'>('Inter');

  const doPreview = useCallback(async () => {
    if (!reportName) return;
    // The SIMPLE dup flow: the report lists every layer, so it is not shown to a user some
    // layer is blind to while the batch is open (owner 2026-10-17, G5 parity: Preview_Execute;
    // the server refuses it too — report-blind).
    if (dupFlow.simple && layers.some(l => isBlind(l, layers, me, dupFlow))) {
      notify.warning(PLM_REPORT_BLIND_TEXT);
      return;
    }
    if (dirty) {
      const ok = await confirm({
        title: 'Unsaved changes',
        message: 'This layer has unsaved changes, which the report will not include. Preview anyway?',
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const opened = await previewPlmReport(batchOid, reportName);
      if (!opened) notify.warning('The report opened in a blocked popup — allow popups for this site.');
    } finally {
      setBusy(false);
    }
  }, [reportName, dirty, confirm, batchOid, notify, dupFlow, layers, me]);

  // ── reset · delete · comment (Phase 4) ─────────────────────────────────────

  /** Blanks every layer in the batch. Destructive and batch-wide, so it is
   *  confirmed in the danger tone before anything happens. */
  /** Prototype v18 ⋯ "Reset unsaved changes": throws away the edits on the open
   *  layer and goes back to its last saved version — other layers' unsaved work
   *  is kept. (Reset below is G5's: it blanks every layer of the batch.) */
  const doDiscardLayer = useCallback(async () => {
    if (!activeSeq) return;
    const layerId = layers.find(l => l.sequenceOid === activeSeq)?.sampleL ?? 'this layer';
    if (!dirty && !pending.current.has(activeSeq)) {
      notify.info('Nothing to reset — no unsaved changes.');
      return;
    }
    const ok = await confirm({
      title: 'Reset',
      message: `Discard every unsaved change on ${layerId} and go back to the last saved version?`,
      confirmLabel: 'Reset',
      tone: 'danger',
    });
    if (!ok) return;
    pending.current.delete(activeSeq);
    syncParked();
    setDirty(false);
    if (pending.current.size === 0) resetUnsavedTracking();
    await loadLayer(activeSeq);
    notify.success('Layer reset to the last saved version.');
  }, [activeSeq, layers, dirty, notify, confirm, syncParked, loadLayer]);

  const doReset = useCallback(async () => {
    const entered = layers.filter(l => l.hasMaterial || l.examCount > 0).length;
    await runDelete({
      formName: AUDIT_FORM,
      title: 'Reset Batch',
      message: entered > 0
        ? `Clear the work on all ${layers.length} layers of this batch? ${entered} already hold data.`
        : `Clear the work on all ${layers.length} layers of this batch?`,
      confirmLabel: 'Reset',
      successMessage: 'Reset completed successfully.',
      delete: async () => { await resetPlmBatch(batchOid); },
      onDeleted: async () => {
        setDirty(false);
        setLayers(await fetchPlmLayers(batchOid));
        setHeader(await fetchPlmBatch(batchOid));
        if (activeSeq) await loadLayer(activeSeq);
      },
    });
  }, [layers, runDelete, batchOid, activeSeq, loadLayer]);

  /* Delete Batch was removed from this toolbar on 2026-09-24 (review). Its
     handler went with it rather than being left dead: a batch is still deleted
     from the QC Batch screen this one opens from, which is where the action
     belongs. `deletePlmBatch` stays in plm.api.ts for that caller. */

  const doComment = useCallback(async (title: string, text: string) => {
    await runSave({
      formName: AUDIT_FORM,
      auditAction: 'edit',
      confirmTitle: 'Comment',
      confirmMessage: 'Save this comment against the batch?',
      successMessage: 'Comment saved successfully.',
      save: async () => { await addPlmComment(batchOid, title, text); },
      onSaved: () => setCommentOpen(false),
    });
  }, [runSave, batchOid]);

  // ── carry over results (Phase 3) ───────────────────────────────────────────

  /** Copies one layer onto others. A copy REPLACES each target entirely, so it
   *  goes through the shared confirm → save → acknowledge flow like any other
   *  destructive write. */
  const doCopy = useCallback(async (sourceSequenceOid: string, targetOids: string[]) => {
    if (targetOids.length === 0) return;
    const from = layers.find(l => l.sequenceOid === sourceSequenceOid)?.sampleL ?? '';
    await runSave({
      formName: AUDIT_FORM,
      auditAction: 'edit',
      confirmTitle: 'Carry Over Results',
      confirmMessage: targetOids.length === 1
        ? `Copy ${from} onto the selected layer? Everything on it will be replaced.`
        : `Copy ${from} onto ${targetOids.length} layers? Everything on them will be replaced.`,
      successMessage: 'Copied successfully.',
      save: async () => { await copyPlmLayer(sourceSequenceOid, targetOids); },
      onSaved: async () => {
        setCarryOpen(false);
        setLayers(await fetchPlmLayers(batchOid));
        // The copy may have rewritten the layer the analyst is looking at.
        if (activeSeq) await loadLayer(activeSeq);
      },
    });
  }, [layers, runSave, batchOid, activeSeq, loadLayer]);

  // ── point count (Phase 2) ──────────────────────────────────────────────────

  /** The asbestos fibers the count covers — the LIVE exam rows, in grid order.
   *  Legacy rebuilds the workbook's columns from these every time PC Entry
   *  opens (PCEntry_Execute :121), so a fiber added since the count was started
   *  gets a column and one that was removed drops out. */
  const asbestosFiberNames = useMemo(() => {
    const asbestosTypeId = lookups.fiberTypes.find(t => t.name === 'Asbestos Fibers')?.id;
    if (!asbestosTypeId) return [];
    return exams
      .filter(e => e.fiberTypeOid === asbestosTypeId && (e.name ?? '').trim())
      .sort((a, b) => a.sort - b.sort)
      .map(e => e.name!.trim());
  }, [exams, lookups.fiberTypes]);

  /** The fibers whose Result the stored point count WROTE — their cells in the
   *  exam grid are read-only and wear the "PC" tag (prototype v8). */
  const pointCountFiberNames = useMemo(() => new Set(
    pointCount.pointCountType ? pointCount.slides.map(s => (s.fiberName ?? '').trim()) : [],
  ), [pointCount]);

  /** The count hangs off the observation row, which does not exist until the
   *  layer has been saved once. */
  const pcDisabled = !context?.observationOid;

  /**
   * The Point Count checkbox and the Type dropdown (prototype v8 `#pcOn` /
   * `#pcType`). They only ARM the count — ticking the box enables Type and
   * "PC Entry…", picking a type sets it — and NOTHING opens. The grid is
   * reached through "PC Entry…" alone. (Until 2026-09-26 both the tick and a
   * type change popped the grid up; owner report: "when I click the checkbox
   * the popup [should] not appear, in the prototype".)
   *
   * The two destructive turns ask first, in the prototype's words:
   *   off, with a count stored   → "Turn off point count?"
   *   400 ⇄ 1000, count stored   → "Change point count type?" — the entered
   *                                 counts are cleared, as the prototype does
   * With nothing stored either is just a local switch.
   */
  /**
   * Clears the stored count and reloads the layer.  Returns whether it happened.
   *
   * With unsaved work on the page it runs as a SAVE instead — the same words,
   * plus a note — writing that work first, because the reload would otherwise
   * overwrite it (standing rule 15; see persistEdits).  Through the save flow so
   * the reason-for-change gate still applies to the layer write.
   */
  const clearPointCount = useCallback(async (
    title: string, message: string, doneMessage?: string,
  ): Promise<boolean> => {
    if (!activeSeq) return false;
    const seq = activeSeq;
    if (hasUnsavedEdits()) {
      return runSave({
        formName: AUDIT_FORM,
        auditAction: 'edit',
        confirmTitle: title,
        confirmMessage: `${message} Your unsaved changes will be saved first.`,
        successMessage: doneMessage,
        save: async () => {
          await persistEdits();
          await clearPlmPointCount(seq);
        },
        onSaved: async () => {
          clearParked();
          const [rows] = await Promise.all([fetchPlmLayers(batchOid), loadLayer(seq), refreshQc()]);
          setLayers(rows);
          setHeader(await fetchPlmBatch(batchOid));
        },
      });
    }
    const ok = await confirm({ title, message, tone: 'danger' });
    if (!ok) return false;
    setBusy(true);
    try {
      await clearPlmPointCount(seq);
      // Refetches the (now empty) count; clearing it rewrites fiber Results, which
      // a re-read verdict can depend on.
      await Promise.all([loadLayer(seq), refreshQc()]);
      if (doneMessage) notify.success(doneMessage);
    } finally {
      setBusy(false);
    }
    return true;
  }, [activeSeq, hasUnsavedEdits, runSave, persistEdits, batchOid, loadLayer, confirm, notify, refreshQc]);

  const onPointCountTypeChange = useCallback(async (next: PointCountType | null) => {
    if (!activeSeq) return;
    const stored = pointCount.slides.length > 0;

    if (next === null) {
      if (!stored) {
        setPointCount(prev => ({ ...prev, pointCountType: null }));
        return;
      }
      // Legacy resets every named asbestos fiber's result to "0" when the count
      // is switched off — say so before doing it.
      await clearPointCount(
        'Turn off point count?',
        'Results taken from the point count will be cleared and become editable visual estimates.',
        'Point count removed successfully.');
      return;
    }

    if (stored && pointCount.pointCountType && pointCount.pointCountType !== next) {
      const cleared = await clearPointCount(
        'Change point count type?',
        `The ${pointCount.pointCountType}-point counts already entered will be cleared.`);
      if (!cleared) return;
    }

    // Arm the new type. Nothing is stored until the analyst opens PC Entry…
    // and presses Apply.
    setPointCount(prev => ({ ...prev, pointCountType: next }));
  }, [activeSeq, clearPointCount, pointCount]);

  const onSavePointCount = useCallback(async (slides: PlmPointCountSlide[]) => {
    if (!activeSeq || !pointCount.pointCountType) return;
    const seq = activeSeq;
    // Unsaved work is saved in the SAME step, BEFORE the count: the count writes
    // its Results onto the SAVED exam rows (an unsaved Chrysotile row gets none),
    // and the reload below would otherwise overwrite the work (see persistEdits).
    const withEdits = hasUnsavedEdits();
    await runSave({
      formName: AUDIT_FORM,
      auditAction: 'edit',
      confirmTitle: 'Point Count',
      confirmMessage: 'Save this point count and update the asbestos results?',
      save: async () => {
        if (withEdits) await persistEdits();
        await savePlmPointCount(seq, pointCount.pointCountType!, slides);
      },
      onSaved: async () => {
        setPcOpen(false);
        if (withEdits) clearParked();
        // The save rewrote the exam Results server-side, so reload rather than
        // patching locally.  A layer write can also flip the layer to "Entered"
        // and the batch status, so the header is re-read with it.
        const [rows] = await Promise.all([fetchPlmLayers(batchOid), loadLayer(seq), refreshQc()]);
        setLayers(rows);
        if (withEdits) setHeader(await fetchPlmBatch(batchOid));
      },
    });
  }, [activeSeq, pointCount.pointCountType, runSave, loadLayer, batchOid, hasUnsavedEdits, persistEdits, refreshQc]);

  // ── exam rows ──────────────────────────────────────────────────────────────

  /** New exam row — pre-filled with the remaining percentage, as legacy does.
   *  `init` carries the Component when the row comes from the quick-add. */
  const addExam = useCallback((init?: Pick<PlmExamInput, 'fiberTypeOid'>) => {
    setExams(prev => {
      const row = blankExam(prev.length + 1);
      const next = [...prev, { ...row, ...init, result: balanceResult(prev) }];
      setExamIndex(next.length - 1);
      return next;
    });
    setTab('exam');
    setDirty(true);
  }, []);

  /** Insert a blank row directly AFTER `i` — the per-row + in the Actions
   *  column. Appending (the toolbar's Add Component) is `addExam`. */
  const addExamAt = useCallback((i: number) => {
    setExams(prev => {
      const row = { ...blankExam(i + 2), result: balanceResult(prev) };
      const next = [...prev.slice(0, i + 1), row, ...prev.slice(i + 1)]
        .map((e, n) => ({ ...e, sort: n + 1 }));
      setExamIndex(i + 1);
      return next;
    });
    setDirty(true);
  }, []);

  /** Move the row at `i` one place up (-1) or down (+1). Legacy keeps the exam
   *  rows in an explicit `sort` order, so a reorder is a swap plus a renumber —
   *  never a re-sort of the array alone, which would not survive a save. */
  const moveExam = useCallback((i: number, dir: -1 | 1) => {
    setExams(prev => {
      const j = i + dir;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const a = prev[i], b = prev[j];
      if (!a || !b) return prev;            // strict-index guard
      const next = [...prev];
      next[i] = b; next[j] = a;
      setExamIndex(j);
      return next.map((e, n) => ({ ...e, sort: n + 1 }));
    });
    setDirty(true);
  }, []);

  const removeExamAt = useCallback(async (i: number) => {
    if (!exams[i]) return;
    const ok = await confirm({
      title: 'Remove',
      message: 'Remove this component row?',
      tone: 'danger',
    });
    if (!ok) return;
    setExams(prev => {
      const next = prev.filter((_, n) => n !== i).map((e, n) => ({ ...e, sort: n + 1 }));
      setExamIndex(Math.max(0, Math.min(i, next.length - 1)));
      return next;
    });
    setDirty(true);
  }, [exams, confirm]);

  const removeExam = useCallback(async () => {
    if (!exams[examIndex]) return;
    const ok = await confirm({
      title: 'Remove',
      message: 'Remove the selected component row?',
      tone: 'danger',
    });
    if (!ok) return;
    setExams(prev => {
      const next = prev.filter((_, i) => i !== examIndex).map((e, i) => ({ ...e, sort: i + 1 }));
      setExamIndex(Math.max(0, Math.min(examIndex, next.length - 1)));
      return next;
    });
    setDirty(true);
  }, [exams, examIndex, confirm]);

  // Counts for the tab pills. The exam count ignores the blank seed row the
  // grid always shows, so an untouched layer reads no count rather than "1".
  const realExamCount = useMemo(
    () => exams.filter(e => !isBlankExam(e)).length, [exams]);

  // CROSS-TAB CONFLICT (prototype v8 `conflictMsg`/`renderConflicts`). The two
  // tabs can contradict each other, and v8 shows it as a PERSISTENT inline
  // warning at the top of BOTH the Stereoscopic and PLM Exam tabs — not only as
  // the save-time confirm doSave already raises. It is non-blocking: which side
  // is right is the analyst's call, so it names both and is confirmed at save.
  const conflictMessage = useMemo(() => {
    if (!draft) return null;
    const asbestosTypeId = lookups.fiberTypes.find(t => t.name === 'Asbestos Fibers')?.id;
    const namedAsbestos = asbestosTypeId
      ? exams.filter(e => e.fiberTypeOid === asbestosTypeId && (e.name ?? '').trim())
      : [];
    // Prototype v8 (`conflictMsg`): the KEY claims are bold — "No Asbestos
    // Detected" and each fibre name — against the banner's semibold base, and it
    // reads "Stereo observation", not "Stereoscopic". Returned as JSX so the
    // <strong> renders (a plain string would print the tags).
    if (draft.noAsbestosDetected && namedAsbestos.length > 0) {
      return (
        <>
          Stereo observation says <strong>No Asbestos Detected</strong>, but the PLM Exam lists{' '}
          {namedAsbestos.map((e, i) => (
            <Fragment key={i}>
              {i > 0 && ', '}
              <strong>{e.name}</strong>
            </Fragment>
          ))}.
        </>
      );
    }
    // Stereo estimates a % but the exam names no asbestos component. A free-text
    // "<1" / "Trace" is not a quantity, so it does not trip this (parseFloat NaN).
    const stereoPct = Number.parseFloat((draft.fiberObservation ?? '').replace(/[<%\s]/g, ''));
    const realComps = exams.filter(e => !isBlankExam(e));
    if (Number.isFinite(stereoPct) && stereoPct > 0
        && namedAsbestos.length === 0 && realComps.length > 0) {
      return (
        <>
          Stereo observation estimates <strong>{draft.fiberObservation}%</strong> asbestos, but the PLM Exam has no Asbestos Fibers component.
        </>
      );
    }
    return null;
  }, [draft, exams, lookups.fiberTypes]);

  const { qcCount, qcFailing } = useMemo(() => {
    const pairs = buildQcPairs(groupLayers(qcRows));
    return { qcCount: pairs.length, qcFailing: pairs.filter(p => p.state === 'fail').length };
  }, [qcRows]);

  // The QC tab's sub-line. Counts the re-reads and how they came out, so the
  // caption answers "is there anything to look at?" without opening the tab.
  const qcSummary = useMemo(() => {
    const pairs = buildQcPairs(groupLayers(qcRows));
    if (!pairs.length) return 'No QC re-reads';
    const fail = pairs.filter(p => p.state === 'fail').length;
    const pending = pairs.filter(p => p.state === 'pending').length;
    if (fail) return `${fail} of ${pairs.length} failing`;
    if (pending) return `${pending} of ${pairs.length} pending`;
    return `${pairs.length} passing`;
  }, [qcRows]);

  // ── Final Confirmation read-back ───────────────────────────────────────────
  // Both derive from state the form already holds, so the tab needs no fetch of
  // its own. The Material Description is read from the DRAFT (not the server's
  // `materialNames`), so a description typed-but-not-yet-saved still reads back.
  const materialNamesForDraft = useMemo(
    () => materialText(draft?.materialIds, lookups.materials).trim() || null,
    [draft?.materialIds, lookups.materials]);

  const pointCountLabel = useMemo(() => {
    const t = pointCount.pointCountType;
    if (!t) return undefined;
    return `${totalPoints(t)} points — ${slideCount(t)} slides × ${pointsPerSlide(t)}`;
  }, [pointCount.pointCountType]);

  // ── layer list ─────────────────────────────────────────────────────────────

  // The prototype's Sequence panel has no search — a batch is one screen of
  // layers, and the header already says where you are in it. v13 (2026-09-28):
  // another analyst's Inter re-read is not listed until they submit it, so the
  // position, the count and prev / next all walk the LISTED layers.
  const shownLayers = useMemo(() => layers.filter(l => seeable(l, me, layers, dupFlow)), [layers, me, dupFlow]);
  const layerIdx = shownLayers.findIndex(r => r.sequenceOid === activeSeq);

  // Right-click a row → Add Intra-dup / Add Inter-dup (owner rule 2026-10-07:
  // dups are selected here, on the entry page, on the sample's own row). Not
  // offered while the batch cannot change — the browser's own menu shows then.
  const [dupMenu, setDupMenu] = useState<{ x: number; y: number; row: PlmLayerRow } | null>(null);
  const openDupMenu = useCallback((e: ReactMouseEvent<HTMLTableCellElement>, row: PlmLayerRow) => {
    setDupMenu({ x: e.clientX, y: e.clientY, row });
  }, []);
  /** Add QC (user feedback C8, 2026-11-02): the same menu, for the open layer, under its button —
   *  the right-click was hard to discover. */
  const addQcBtn = useRef<HTMLButtonElement>(null);
  const openAddQc = useCallback((row: PlmLayerRow | null | undefined) => {
    const box = addQcBtn.current?.getBoundingClientRect();
    if (row && box) setDupMenu({ x: box.left, y: box.bottom + 2, row });
  }, []);
  const rowMenu = batchLocked || viewMode ? undefined : openDupMenu;

  const layerColumns = useMemo<AppColumn<PlmLayerRow>[]>(() => [
    {
      field: 'sampleL', header: 'SysSampleCode', width: 150,
      // noClip — the prototype's rail NEVER truncates the code: its cells are
      // `white-space:nowrap` on an AUTO-layout table, so the column grows to the
      // content and the rail scrolls sideways if it must. Ours was clipping to
      // "26092300…", which hides the very thing the column exists for — WHICH
      // LAYER the row is (owner report 2026-09-25, side-by-side with the
      // prototype). The code is the row's identity; the Status pill beside it is
      // three words that never grow.
      noClip: true,
      onCellContextMenu: rowMenu,
      text: r => r.sampleL ?? '',
      // Prototype v8: the code, then its QC-type chip INLINE on the same line
      // (INTRA / INTER / REF). The chip is what tells an analyst at a glance
      // which rows in the sequence are QC and which are the samples — reading
      // that off the long "…Intra Re-read.A" suffix is much slower.
      // A dirty marker rides beside the code when this is the open, edited row.
      render: r => {
        const isOpen = r.sequenceOid === activeSeq;
        const qc = (r.qcType ?? '').trim();
        const chip = /^Intra/i.test(qc) ? 'INTRA' : /^Inter/i.test(qc) ? 'INTER' : null;
        // Show the BASE code and let the chip carry the QC type, exactly as the
        // prototype's rail does: "260923002-001Inter-dup.A" (and the older
        // "…Inter Re-read.A") becomes "260923002-001.A" + INTER. The long form
        // wrapped to three lines in a rail this width, and the words it wrapped
        // are the ones the chip already says. Stripping is display-only — every
        // id sent to the API, the QC pairing and the report is untouched.
        const code = (r.sampleL ?? '').replace(/\s*(Intra|Inter)[\s-]*(Re-?read|dup)\s*/i, '');
        // The SIMPLE dup flow (2026-10-17): a layer blind to this user says so in the list.
        const blindRow = dupFlow.simple && isBlind(r, layers, me, dupFlow);
        return (
          <span className="plm-lrow">
            <span className="plm-lrow__top">
              <span className="plm-lrow__id" title={dupLabel(r.sampleL)}>{code}</span>
              {chip && (
                <span className={`plm-lrow__qc plm-lrow__qc--${chip.toLowerCase()}`}>{chip}</span>
              )}
              {blindRow && (
                <span className="plm-lrow__blind" data-testid="plm-row-blind"
                  title="Blind — hidden until the batch is submitted">🔒 Blind</span>
              )}
              {isOpen && dirty && (
                <span className="plm-lrow__dot" title="Unsaved changes on this layer" />
              )}
            </span>
          </span>
        );
      },
    },
    {
      // Prototype v8 labels the rail pill from its FOUR-STAGE vocabulary
      // (`STAGES`), not from the raw legacy enum: statuses 0 and 1 (Pending
      // Result Entry / Pending Sample Prep) both read "Pending Entry", because
      // either way the analyst has not entered the layer. The docked island
      // already collapses them that way, so the rail took the long legacy text
      // while the island beside it said something shorter for the same layer —
      // and the two now share ONE helper so they cannot drift (owner report
      // 2026-09-25, marked on the prototype). It is also narrower, which is what
      // the rail's fixed 330px needs.
      field: 'statusText', header: 'Status', width: 110, type: 'status',
      onCellContextMenu: rowMenu,
      text: r => plmStageLabel(r.statusText),
    },
  ], [activeSeq, dirty, rowMenu, dupFlow, layers, me]);

  const activeLayer = layers.find(l => l.sequenceOid === activeSeq) ?? null;

  // ── v13 ownership / blind rules for THIS user (plmWorkflow; the server's
  //    PlmLayerRules is authoritative and refuses anything these would allow) ──
  const nameOf = useCallback((oid: string | null | undefined) =>
    (oid ? lookups.analysts.find(a => a.id.toLowerCase() === oid.toLowerCase())?.name : null)
      ?? layers.find(l => l.analyzedByOid === oid)?.analyzedByName ?? null, [lookups.analysts, layers]);
  const activeBlind = activeLayer ? isBlind(activeLayer, layers, me, dupFlow) : false;
  // Today's Daily QC lets this analyst enter results (no status = not gated). While the
  // Daily QC is optional (AI_AppSettings 'PlmDailyQcGate' not 'Enforce', 2026-10-17)
  // nothing is locked for it and the Daily QC ID is not required.
  const dailyEnforced = !!lookups.dailyQcEnforced;
  const dailyOk = dailyAllows(dailyStatus, dailyEnforced);
  const dailyRequired = dailyQcIdRequired(dailyStatus, dailyEnforced);
  const layerLock = activeLayer
    ? lockReason(activeLayer, layers, me, nameOf(lockOwnerOid(activeLayer, layers)), dupFlow)
      ?? dailyLockReason(activeLayer, me, dailyOk)
    : null;
  /** Everything that edits the LAYER on screen: the batch is closed, the batch
   *  was opened in View mode, or the layer is locked for this user. */
  const readOnly = batchLocked || viewMode || !!layerLock;

  // A layer that opens with no Daily QC ID is offered the read its analyst made on the
  // day of analysis — a passed one first, else the latest (the server's layerDailyQcId) —
  // also on a read-only layer and on another analyst's layer (owner 2026-10-17, G5 parity:
  // PlmV2WorkspaceController.LoadLayer). A layer the signed-in analyst reads that has none
  // is offered their Daily QC of today. On a layer the user may edit only a PASSED read is
  // filled in. Offered once each time the layer loads, so emptying the box sticks until
  // then, and not an edit (dirty stays as it was).
  useEffect(() => {
    if (!activeSeq || !draft || draft.dailyQcId || dailyOffered.current.has(activeSeq)) return;
    if (!layerDaily.current.has(activeSeq)) return;          // the layer's own answer is not in yet
    if (!readOnly && dailyChoices.length === 0) return;       // the passed reads are not in yet
    const mine = !readOnly && (!draft.analyzedByOid || (!!me && draft.analyzedByOid.toLowerCase() === me.toLowerCase()));
    const offered = offeredDailyQcId({
      layerOffer: layerDaily.current.get(activeSeq) ?? null, mine, readOnly, status: dailyStatus, choices: dailyChoices,
    });
    if (offered === undefined) return;                        // today's read is not in yet
    dailyOffered.current.add(activeSeq);
    if (offered) setDraft(d => (d && !d.dailyQcId ? { ...d, dailyQcId: offered } : d));
  }, [activeSeq, draft, readOnly, dailyChoices, dailyStatus, me]);
  /** Batch-level actions (Submit, Reset) — not the per-layer lock. */
  const batchReadOnly = batchLocked || viewMode;
  /** Why Add QC is greyed on the open layer: neither dup can be added. A dup row keeps the
   *  button, because the same menu claims and removes it. Same rules as doAddDup. */
  const addQcBlock = activeLayer && !activeLayer.reReadKind
    ? (dupBlock(activeLayer, layers, me, 'Intra', nameOf(activeLayer.analyzedByOid))
        && dupBlock(activeLayer, layers, me, 'Inter', nameOf(activeLayer.analyzedByOid)))
    : null;
  /** An open QC dup may be removed (Bin Yu 2026-10-15) by its own analyst or the
   *  sample's analyst — also when the row is otherwise read-only to them (an
   *  Inter-dup nobody has started). The server checks the same. */
  const canRemoveDup = useMemo(() => {
    const row = layers.find(l => l.sequenceOid === activeSeq);
    // The SIMPLE flow closes a read only by the batch Submit — removable again after a Rollback.
    if (!row?.reReadKind || (row.reReadSubmitted && dupSteps) || batchReadOnly || !me) return false;
    // The dup's owner = its re-read analyst, else whoever entered it (owner 2026-10-17, G5 OwnerOid).
    return ownerOf(row) === me || originalAnalyst(row, layers) === me;
  }, [layers, activeSeq, batchReadOnly, me, dupSteps]);

  // ── header: Analyzed By / Analyzed Date (owner rule 2026-10-07) ───────────
  // Both are stamped by the save, not picked: the date on every save, the
  // analyst on the first. The server does that for a signed-in analyst
  // (PlmController.StampAnalystAsync — the same test as here), so for them the
  // two are read-only. A caller who is not an analyst keeps the editors,
  // because for them the server saves exactly what it is sent.
  const autoStamp = !!me && lookups.analysts.some(a => a.id.toLowerCase() === me.toLowerCase());
  /** Entered = saved at least once. Opening an entered layer is an EDIT, and
   *  only then may the analyst's name be changed. */
  const layerEntered = (activeLayer?.status ?? 0) >= 2;
  const analystEditable = !autoStamp || layerEntered;
  // A layer still pending shows who and when it WILL be stamped with as soon as
  // it opens — for the analyst who may enter it.
  const previewStamp = autoStamp && !layerEntered && !readOnly;
  const shownAnalyzedDate = draft?.analyzedDate
    ?? (previewStamp && activeSeq ? openedAt.current.get(activeSeq) ?? null : null);
  const shownAnalystName = nameOf(draft?.analyzedByOid ?? (previewStamp ? me : null));
  /** The Entered Date is the sample's Received date (owner rule 2026-10-08). */
  const enteredDateShown = context?.receivedDate ?? context?.enteredDate ?? null;
  // A re-read on screen that belongs to this user and was started from PLM (it
  // has a record, so it can be submitted / re-opened) — Submit read / Re-open.
  // The SIMPLE flow has no Submit Read / Re-open Read: the batch Submit closes the reads.
  const myReRead = dupSteps && activeLayer?.reReadKind && activeLayer.qcId && ownerOf(activeLayer)
    && me && ownerOf(activeLayer)!.toLowerCase() === me.toLowerCase() ? activeLayer : null;

  // ── Validate mode (2026-09-29, prototype v13) ────────────────────────────
  // Why Validate is refused up front (not pending / your own batch while No
  // Self-Review is on) and how many check points are still unanswered — the
  // server enforces both; these only let the button say so.
  const valBlock = validateMode ? validateBlock(header?.status, layers, me, noSelfReview) : null;
  /** Reject needs only a batch still pending validation — the analyst may send
   *  their own batch back (prototype v13). */
  const valPending = header?.status === PLM_PENDING_VALIDATION;
  const cpUnanswered = unansweredCheckPoints(checkPoints, cpEdits);
  const valDisabledWhy = valBlock
    ?? (cpUnanswered > 0 ? `${cpUnanswered} check point(s) still need Yes / No / N/A (Check Points tab).` : null);
  const onCheckPointEdit = useCallback((id: string, patch: Partial<SdmsCheckPointEdit>) => {
    setCpEdits(prev => new Map(prev).set(id, { ...(prev.get(id) ?? { id }), ...patch, id }));
  }, []);
  const valTarget = useMemo(
    () => [{ id: batchOid, label: header?.analyticalBatchId ?? batchOid }], [batchOid, header?.analyticalBatchId]);
  /** Validate this batch — the answers given on the Check Points tab are saved
   *  first; on success the validator goes back to the Validate list. */
  const doValidate = useCallback(async () => {
    if (await runValidate(valTarget, [...cpEdits.values()])) {
      resetUnsavedTracking();
      (onValidateDone ?? onBack)();
    }
  }, [runValidate, valTarget, cpEdits, onBack, onValidateDone]);
  /** Reject = return the batch to the analyst (reason required, audited). */
  const doReject = useCallback(async () => {
    if (await runReject(valTarget)) {
      resetUnsavedTracking();
      (onValidateDone ?? onBack)();
    }
  }, [runReject, valTarget, onBack, onValidateDone]);

  // The Inter-dup bar — the waiting dup the second analyst will read.
  // None in the SIMPLE flow: nobody "starts" an Inter-dup there — the first eligible
  // analyst to save it owns it.
  const interOptions = useMemo(() => (dupSteps ? interCandidates(layers, me) : []), [layers, me, dupSteps]);
  const [interPick, setInterPick] = useState('');
  useEffect(() => {
    setInterPick(p => (interOptions.some(l => l.sequenceOid === p) ? p : (interOptions[0]?.sequenceOid ?? '')));
  }, [interOptions]);

  /** Start reading the picked Inter-dup (owner rule 2026-10-07): the original
   *  analyst added the row, and STARTING it makes it this analyst's — from then
   *  on the original is hidden from them until they submit. The server re-checks
   *  every rule: not your own batch, every sample entered, the permission. */
  const doStartInterdup = useCallback(async (picked?: PlmLayerRow) => {
    const row = picked ?? layers.find(l => l.sequenceOid === interPick);
    if (!row) return;
    const code = dupLabel(row.sampleL);
    const ok = await confirm({
      title: 'Start Inter-dup',
      message: `Start reading ${code}? You read it blind — the original stays hidden from you `
        + 'until you submit your read.',
      confirmLabel: 'Start',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await addPlmReRead(row.sequenceOid, 'Inter');
      notify.success(`${code} started — blind.`);
      await loadShell();
      stashActive();
      setActiveSeq(res.sequenceOid || row.sequenceOid);
    } finally {
      setBusy(false);
    }
  }, [layers, interPick, confirm, notify, loadShell, stashActive]);

  /** Add an Intra-dup or an Inter-dup under a sample row — the Sequence list's
   *  right-click menu (owner rule 2026-10-07). Both are selected by the analyst
   *  who analysed the sample, once every sample layer is entered. An Intra-dup
   *  is their own second read, so it opens; an Inter-dup waits, unowned, for a
   *  second analyst to start it. */
  const doAddDup = useCallback(async (source: PlmLayerRow, kind: 'Intra' | 'Inter') => {
    const name = `${kind}-dup`;
    const block = dupBlock(source, layers, me, kind, nameOf(source.analyzedByOid));
    if (block) { notify.warning(block); return; }
    // The rules read SAVED data ("every sample layer is entered").
    if (hasUnsavedEdits()) { notify.warning(`Save your changes before adding an ${name}.`); return; }
    const ok = await confirm({
      title: `Add ${name}`,
      message: kind === 'Inter'
        ? dupSteps
          ? `Add an Inter-dup under ${source.sampleL}? A second analyst reads it blind.`
          : `Add an Inter-dup under ${source.sampleL}? Another analyst reads it blind — the first to save it — `
            + 'and the sample stays hidden from everyone but you until the batch is submitted.'
        : `Add an Intra-dup under ${source.sampleL}? You read the sample a second time yourself.`,
      confirmLabel: 'Add',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await addPlmReRead(source.sequenceOid, kind);
      notify.success(kind === 'Inter'
        ? `${name} added under ${source.sampleL} — a second analyst reads it.`
        : `${name} added under ${source.sampleL}.`);
      await loadShell();
      if (kind === 'Intra' && res.sequenceOid) setActiveSeq(res.sequenceOid);
    } finally {
      setBusy(false);
    }
  }, [layers, me, nameOf, notify, hasUnsavedEdits, confirm, loadShell, dupSteps]);

  /**
   * Rollback — un-submit the batch and reopen it for result entry.
   *
   * It is the QC-BATCH rollback (`POST qc-batches/{id}/rollback` →
   * dbo.AI_Analysis_QcBatch_Rollback), the one the QC Batch screens use and the
   * one that knows a PLM batch: the batch returns to Pending Submission, its
   * Data Package is reopened, the samples go back to result entry, and a layer
   * that was Entered STAYS Entered (owner rule 2026-10-08 — its results are
   * still there to correct). The only refusal is a sample already Reported.
   *
   * Until 2026-10-08 this button opened the SDMS sheet's rollback dialog, which
   * is for SDMS batches: it accepted a PLM batch only at two of its statuses
   * (a batch at Result Validated was refused), it reset every layer to Pending
   * Entry, and the screen never read its answer — a refusal was announced as
   * "Rolled Back" (owner report: "rollback not working in view mode").
   *
   * Reason required and audited, as on the QC Batch screens. Nothing is said to
   * have happened until the server says it did.
   */
  const doRollback = useCallback(async () => {
    if (!header) return;
    const reason = await prompt({
      title: 'Rollback',
      message: `Roll back ${header.analyticalBatchId ?? 'this batch'}? It is un-submitted and reopened for `
        + 'result entry. Reason (required — audited):',
      placeholder: 'Rollback reason',
      required: true,
      confirmLabel: 'Rollback',
    });
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await qcBatchApi.rollback(batchOid, reason.trim());
    } catch (e) {
      await showDialog({
        type: 'error',
        title: 'Rollback',
        message: (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'The batch could not be rolled back. Try Refresh, and if it keeps happening report it.',
      });
      return;
    } finally {
      setBusy(false);
    }
    // The batch is editable again — in Enter mode, which a batch rolled back
    // from View is switched to so the analyst can carry on.
    if (viewMode && !validateMode) onEnterMode?.();
    await loadShell();
    if (activeSeq) await loadLayer(activeSeq);
    // A rollback REVERSES a submission, so it is a milestone, not routine chatter
    // — the centred dialog, acknowledged (§18.6 two-tier standard).
    void showDialog({
      type: 'success',
      title: 'Rolled Back',
      message: 'This batch has been reopened for result entry.',
    });
  }, [header, prompt, batchOid, viewMode, validateMode, onEnterMode, loadShell, activeSeq, loadLayer]);

  /** Submit this user's re-read: the blind is lifted, an Inter read locks. */
  const doSubmitReRead = useCallback(async () => {
    if (!myReRead) return;
    if (dirty) { notify.warning('Save your read before submitting it.'); return; }
    const inter = myReRead.reReadKind === 'Inter';
    const ok = await confirm({
      title: 'Submit read',
      message: inter
        ? `Submit your blind Inter-dup read ${dupLabel(myReRead.sampleL)}? The original becomes visible to you and `
          + 'your read is locked — re-opening it is audited and marks it Not blind.'
        : `Submit your Intra-dup read ${dupLabel(myReRead.sampleL)}?`,
      confirmLabel: 'Submit',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await submitPlmReRead(myReRead.sequenceOid);
      notify.success('Submit completed successfully.');
      await loadShell();
    } finally {
      setBusy(false);
    }
  }, [myReRead, dirty, confirm, notify, loadShell]);

  /** Re-open this user's submitted re-read — reason required, audited. */
  const doReopenReRead = useCallback(async () => {
    if (!myReRead) return;
    const reason = await prompt({
      title: 'Re-open read',
      message: myReRead.reReadKind === 'Inter'
        ? 'Re-opening a submitted Inter-dup read marks its QC comparison "Not blind". Reason (required — audited):'
        : 'Reason for re-opening (required — audited):',
      placeholder: 'Reason',
      required: true,
      confirmLabel: 'Re-open',
    });
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await reopenPlmReRead(myReRead.sequenceOid, reason.trim());
      notify.success('Re-open completed successfully.');
      await loadShell();
    } finally {
      setBusy(false);
    }
  }, [myReRead, prompt, notify, loadShell]);

  /** Toolbar Daily QC (owner 2026-10-19, G5 PlmV2DailyQc): always the PLM Daily
   *  QC list. Unsaved edits stay — nothing opens until they are saved. */
  const openDailyQc = useCallback(() => {
    if (hasUnsavedEdits()) { notify.warning('Save your changes before opening the Daily QC.'); return; }
    // With a way back (owner 2026-11-03, "like the My Jobs dashboard has"): the shared
    // "Back to PLM Result Entry" bar (Alt+M) reopens THIS batch, in this mode, on this layer.
    navigateToPage('plm-dailyqc', undefined, {
      returnTo: {
        page: 'plm',
        label: 'PLM Result Entry',
        detail: header?.analyticalBatchId
          ? `Batch ${header.analyticalBatchId}${activeLayer?.sampleL ? ` · ${activeLayer.sampleL}` : ''}`
          : undefined,
        recordId: batchOid,
        context: { batchOid, mode, ...(activeSeq ? { seq: activeSeq } : {}) },
      },
    });
  }, [hasUnsavedEdits, notify, header?.analyticalBatchId, activeLayer?.sampleL, batchOid, mode, activeSeq]);

  /** Supervisor override of the signed-in analyst's FAILED Daily QC today
   *  (needs the override permission — reason required, audited). */
  const doDailyOverride = useCallback(async () => {
    if (!me) return;
    const reason = await prompt({
      title: 'Supervisor override',
      message: 'Allow analysis today although the Daily QC failed? Reason + corrective action (required — audited):',
      placeholder: 'Reason + corrective action',
      required: true,
      confirmLabel: 'Override',
    });
    if (!reason?.trim()) return;
    setBusy(true);
    try {
      await overridePlmDailyQc(me, reason.trim());
      notify.success('Override completed successfully.');
      setDailyStatus(await fetchPlmDailyQcStatus());
    } finally {
      setBusy(false);
    }
  }, [me, prompt, notify]);

  /** True when the OPEN layer is a QC re-read. Carry Over is refused on one:
   *  a re-read exists to be an INDEPENDENT second look, and copying the
   *  original's results onto it would make every QC check pass by
   *  construction — the comparison would be measuring a copy against itself.
   *  (Prototype v8 design note: "Carry Over is disabled on re-read layers,
   *  because a QC re-read must be independent.") */
  const activeIsReRead = /re-?read|dup/i.test(activeLayer?.qcType ?? '');

  /** The ORIGINAL layer a QC re-read re-reads — prototype v8's QC-mode header
   *  "Source Layer" / "Reference ID". Derived from the batch's QC pairs, which
   *  are already loaded (qcRows), so this needs no extra fetch. */
  const sourceLayerId = useMemo(() => {
    if (!activeSeq || !activeIsReRead) return null;
    const pair = buildQcPairs(groupLayers(qcRows))
      .find(p => p.reRead.sequenceOid === activeSeq);
    return pair?.original?.sampleLayer ?? null;
  }, [qcRows, activeSeq, activeIsReRead]);

  // ── keyboard (standing rule 14) ────────────────────────────────────────────
  // The shared toolbar registers Preview itself; these are the PLM verbs,
  // declared next to the buttons that mirror them.
  //
  // EVERY binding is registered whatever the batch state, with `disabled`
  // mirroring its button EXACTLY — the cheat sheet then lists a disabled key
  // greyed, as on every other form. Until 2026-09-26 Save / Add Layer / PC Entry
  // / Carry Over / Submit were only registered on an editable batch, so a
  // read-only batch's `?` sheet dropped them although their buttons were on
  // screen (owner report "it is missing something"). Also fixed that day:
  //   · PC Entry was on Alt+P — Preview's reserved key, which the shared
  //     toolbar also registers — so Alt+P did whichever registered last. Now
  //     Alt+T, free on this screen.
  //   · Back (Esc), Refresh (Alt+R, already advertised in the ⋯ menu but never
  //     bound) and previous / next layer (Alt+← / Alt+→, the top-bar arrows) had
  //     no key, although rule 14 reserves exactly those bindings.
  const PC_ENTRY_KEY = 'alt+t';
  const refreshAll = () => {
    void loadShell();
    if (activeSeq) void loadLayer(activeSeq);
  };
  const prevLayer = layerIdx > 0
    ? () => { void selectLayer(shownLayers[layerIdx - 1]!.sequenceOid); } : undefined;
  const nextLayer = layerIdx >= 0 && layerIdx < shownLayers.length - 1
    ? () => { void selectLayer(shownLayers[layerIdx + 1]!.sequenceOid); } : undefined;
  const hotkeys: HotkeyDef[] = [
    {
      combo: 'mod+s', label: 'Save', group: 'PLM', order: 0,
      disabled: !activeSeq || !anyUnsaved || (readOnly && parkedKeys.length === 0),
      run: () => { void doSave(); },
    },
    {
      combo: 'alt+u', label: 'Submit', group: 'PLM', order: 1,
      disabled: batchReadOnly || layers.length === 0 || busy,
      run: () => { void doSubmit(); },
    },
    validateMode ? {
      // Validate mode: Reject IS the Rollback action (standing rule 14, Alt+B).
      combo: 'alt+b', label: 'Reject', group: 'PLM', order: 2,
      disabled: !valPending || busy,
      run: () => { void doReject(); },
    } : {
      // Alt+B is the reserved Rollback key; the button is live only once the
      // batch is submitted (read-only), which is exactly when Submit is not.
      combo: 'alt+b', label: 'Rollback', group: 'PLM', order: 2,
      disabled: !batchLocked || busy,
      run: () => { void doRollback(); },
    },
    {
      combo: 'alt+v', label: 'Validate', group: 'PLM', order: 2.5,
      disabled: !validateMode || !!valDisabledWhy || busy,
      run: () => { void doValidate(); },
    },
    {
      combo: 'esc', label: 'Back', group: 'PLM', order: 3, allowInInput: false,
      run: onBack,
    },
    // Alt+R (Refresh) comes with the toolbar's own Refresh button since 2026-10-19 (AppToolbar onRefresh).
    {
      // ALWAYS enabled, and a no-op at the ends. In a browser Alt+← is
      // history-Back, and the store only swallows a key it runs — a DISABLED
      // binding on the first layer would let the browser leave PLM altogether.
      combo: 'alt+left', label: 'Previous layer', group: 'PLM', order: 5,
      run: () => prevLayer?.(),
    },
    {
      combo: 'alt+right', label: 'Next layer', group: 'PLM', order: 6,
      run: () => nextLayer?.(),
    },
    {
      combo: 'alt+1', label: 'Stereoscopic Observation tab', group: 'PLM', order: 7,
      run: () => setTab('stereo'),
    },
    {
      combo: 'alt+2', label: 'PLM Exam tab', group: 'PLM', order: 8,
      run: () => setTab('exam'),
    },
    {
      combo: 'alt+l', label: 'Add Layer', group: 'PLM', order: 9,
      disabled: readOnly || !activeSeq || busy,
      run: () => { void doAddLayer(); },
    },
    {
      combo: 'alt+q', label: 'Add QC', group: 'PLM', order: 9,
      disabled: batchReadOnly || !activeLayer || busy || !!addQcBlock,
      run: () => openAddQc(activeLayer),
    },
    {
      combo: 'alt+y', label: 'Carry Over Results', group: 'PLM', order: 10,
      disabled: readOnly || layers.length < 2 || !activeSeq || busy || activeIsReRead,
      run: () => setCarryOpen(true),
    },
    {
      combo: PC_ENTRY_KEY, label: 'PC Entry', group: 'PLM', order: 11,
      disabled: readOnly || pcDisabled || pointCount.pointCountType === null || busy,
      run: () => { setTab('exam'); setPcOpen(true); },
    },
    // The rest of the bar (owner 2026-10-19, "shortcut keys to all the PLM forms").
    { combo: 'alt+3', label: 'QC Result View tab', group: 'PLM', order: 12, run: () => setTab('qc') },
    { combo: 'alt+4', label: 'Final Confirmation tab', group: 'PLM', order: 13, run: () => setTab('final') },
    { combo: 'alt+c', label: 'QC Charts', group: 'PLM', order: 14, disabled: chartsOpen || !!dailyOpen, run: () => setChartsOpen(true) },
    { combo: 'alt+k', label: 'Daily QC', group: 'PLM', order: 15, disabled: chartsOpen || !!dailyOpen, run: openDailyQc },
    { combo: 'alt+m', label: 'Comment', group: 'PLM', order: 16, disabled: busy || chartsOpen || !!dailyOpen, run: () => setCommentOpen(true) },
    { combo: 'alt+z', label: 'Reset batch', group: 'PLM', order: 17,
      disabled: batchReadOnly || layers.length === 0 || busy || chartsOpen || !!dailyOpen, run: () => { void doReset(); } },
  ];
  useHotkeys(hotkeys);

  // Hide the navigation while PLM is open (owner request 2026-09-24): this is
  // a wide, eight-column entry form and the rail plus the working area want
  // every pixel. The user can still reopen the nav by hand, with Ctrl+backslash,
  // or with this form's own FullScreen button. Through the COUNTED shared hook
  // (2026-11-03): PlmPage holds it too, so leaving the batch for a PLM list —
  // or closing a Daily QC read opened from here — no longer reopens the nav.
  useNavCollapse();

  // Top-bar title — PLM swaps in over the QC Batch detail WITHOUT a route change,
  // so without this the top bar keeps showing "QC Batch" (user report 2026-09-23).
  // Legacy titles this screen "PLM" outright (breadcrumb Reminder Activity / QC
  // Batch / QC Batches / PLM).  QcBatchDetailPage stands down while `plmOpen`.

  // FullScreen (legacy toolbar action) = collapse the left navigation panel, which
  // is literally what legacy's does: PLMViewController :356 runs `plmuirefresh()`,
  // and that is `OnClick('LPcell', 'separatorImage', true)` — the nav splitter
  // (Default.aspx :829).  So this drives the SHARED sidebar toggle (standing rule
  // 8) rather than a bespoke overlay, and mirrors the global Ctrl+\ binding.
  // The state comes from AppShell's broadcast so the button reads correctly no
  // matter who flipped it (button, Ctrl+\, or the QC detail's auto-collapse).
  const [navCollapsed, setNavCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('sidebarCollapsed') === 'true'; } catch { return false; }
  });

  // The toolbar's "⋯" overflow (prototype v8). Declared here rather than
  // inline so it can sit in the toolbar's beforeRefresh slot — the prototype
  // puts it at the END of the left group, after QC Charts.
  const moreMenu = (
    <ActionMenu
      title="More actions"
      items={[
        {
          id: 'comment', label: 'Comment', icon: 'ti-message',
          hotkey: formatCombo(effectiveCombo('alt+m')),
          disabled: busy,
          onSelect: () => setCommentOpen(true),
        },
        {
          id: 'fullscreen',
          label: navCollapsed ? 'Exit full screen' : 'Full screen',
          icon: 'ti-maximize',
          hotkey: formatCombo(effectiveCombo('ctrl+\\')),
          onSelect: () => window.dispatchEvent(new Event('ai:nav-toggle')),
        },
        // The Sequence list's right-click menu, for the OPEN layer — the same
        // two actions where a keyboard or a touch screen can reach them.
        {
          id: 'add-intra', label: 'Add Intra-dup', icon: 'ti-copy-plus',
          disabled: batchReadOnly || !activeLayer || busy,
          onSelect: () => { if (activeLayer) void doAddDup(activeLayer, 'Intra'); },
        },
        {
          id: 'add-inter', label: 'Add Inter-dup', icon: 'ti-users-plus',
          disabled: batchReadOnly || !activeLayer || busy,
          onSelect: () => { if (activeLayer) void doAddDup(activeLayer, 'Inter'); },
        },
        {
          id: 'discard', label: 'Reset unsaved changes', icon: 'ti-arrow-back-up',
          disabled: !activeSeq || busy || (!dirty && !parkedKeys.includes(activeSeq)),
          onSelect: () => { void doDiscardLayer(); },
        },
        {
          id: 'reset', label: 'Reset batch', icon: 'ti-rotate-2',
          hotkey: formatCombo(effectiveCombo('alt+z')),
          disabled: batchReadOnly || layers.length === 0 || busy,
          onSelect: () => { void doReset(); },
        },
        // Prototype v18 ⋯ menu: the slides and limits the QC is judged against.
        // The shell's own navigation (and its unsaved-changes guard) opens it.
        {
          id: 'reflib', label: 'Reference Library', icon: 'ti-books',
          onSelect: () => window.dispatchEvent(
            new CustomEvent('ai:navigate', { detail: { page: 'set-plm-reference-library' } })),
        },
      ]}
    />
  );

  useEffect(() => {
    const h = (e: Event) => setNavCollapsed(!!(e as CustomEvent<boolean>).detail);
    window.addEventListener('ai:nav-state', h as EventListener);
    return () => window.removeEventListener('ai:nav-state', h as EventListener);
  }, []);

  // ── render ─────────────────────────────────────────────────────────────────


  /** The layer lifecycle for the docked island (prototype v8 `STAGES`).
   *
   *  Driven by the LAYER's status, not the batch's: a batch holds layers at
   *  different stages, and the island is about the record on screen. Status 0/1
   *  are both "Pending Entry" (Pending Result Entry / Pending Sample Prep — the
   *  analyst has not entered it either way); 2 is Entered. Validated belongs
   *  to Data Review, so on an Entered layer it stays 'todo' rather than being
   *  shown as reached. */
  const islandSteps: IslandStep[] = useMemo(() => {
    const layerAt = (activeLayer?.status ?? 0) >= 2 ? 1 : 0;
    // Once the BATCH is validated (Status 5) its layers stay Entered, so the batch moves the island on
    // (owner 2026-10-19, "validated but still showing Entered"). An APPROVED batch has no step of its own
    // any more (Approved removed 2026-11-03), so it is past the last one: every step reads done.
    const batchAt = /approved/i.test(header?.statusText ?? '') ? 3 : (header?.status ?? 0) >= 5 ? 2 : 0;
    const at = Math.max(layerAt, batchAt);
    return [...PLM_STAGES].map((label, i) => ({
      id: label,
      label,
      state: i < at ? 'done' : i === at ? 'active' : 'todo',
    }));
  }, [activeLayer?.status, header?.status, header?.statusText]);

  //
  // Prototype v8 names the BATCH and the open LAYER here, because the breadcrumb
  // and the big on-page title are gone — this is now the only place the analyst
  // reads which record they are in.
  const topbarTitle = header?.analyticalBatchId
    ? `PLM – Batch ${header.analyticalBatchId}`
      + (activeLayer?.sampleL ? ` · ${activeLayer.sampleL}` : '')
    : 'PLM';
  // Hovering the title gives the batch's CONTEXT, as the prototype does (v13
  // :1205): "QC Batch … · Job … · N in sequence" — the title itself is capped
  // and may end in "…" (owner screenshot 2026-09-29).
  const topbarTooltip = header?.analyticalBatchId
    ? batchTitleTooltip(header.analyticalBatchId, header.jobIds, shownLayers.length)
    : null;
  // Released while Daily QC or QC Charts is swapped in over the batch: Daily QC
  // names itself, and the batch title left behind read as if you were still on
  // the batch (owner screenshot 2026-09-29). Re-claimed on the way back.
  const titleReleased = !!dailyOpen || chartsOpen;
  useEffect(() => {
    if (titleReleased) return;
    window.dispatchEvent(new CustomEvent('ai:topbar-title', {
      detail: topbarTooltip ? { title: topbarTitle, tooltip: topbarTooltip } : topbarTitle,
    }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, [topbarTitle, topbarTooltip, titleReleased]);

  // Guard: a non-PLM batch has no business on this screen (legacy routes to the
  // SDMS sheet instead), so say so rather than rendering an empty shell.
  if (header && !header.isPlmTest) {
    return (
      <div className="plm-root plm-root--message">
        <button className="ca-btn" onClick={onBack}>← Back</button>
        <p>
          Batch <strong>{header.analyticalBatchId}</strong> is not an asbestos PLM
          batch — its test is not configured as an Asbestos_PLM unconventional
          analysis, so it is entered on the SDMS sheet instead.
        </p>
      </div>
    );
  }

  /**
   * The action bar, rendered at the TOP and again at the BOTTOM (prototype v8's
   * `.actbar` + `.actbar.bottom`; the same top-and-bottom shape the shared
   * <SettingsDetailPage> uses, standing rule 12). The stereo tab is tall enough
   * that scrolling to the foot of it would otherwise mean scrolling back up to
   * Save. Both copies are the SAME element with the same handlers, so they can
   * never drift; the hotkey layer is last-registered-wins over identical
   * actions, so the keys behave exactly as before.
   */
  // The read-only and unsaved notes ride the TOP action bar as compact chips (owner 2026-10-19, the same as
  // the Daily QC form: a full-width strip each took a row); the full text is the tooltip.
  // Validate mode names itself first: its batch is always past entry, and "read-only" alone would not say
  // what the screen is FOR.
  // A submitted batch waiting for validation offers "Go to Validation" beside the read-only note (owner
  // 2026-11-03) — to every user, the analyst included ("the same user can validate").
  const offerValidate = !validateMode && valPending && !!onGoToValidation;
  const roNote = validateMode
    ? `Validate mode — review only; use Validate or Reject.${valBlock && valPending ? ` ${valBlock}` : ''}`
    : batchLocked && header
    ? `This batch is ${header.statusText ?? 'past result entry'} and is read-only.`
    : viewMode ? 'View mode — read-only. Switch to Enter to change results.'
      : layerLock && !activeBlind ? `🔒 ${layerLock}` : null;
  const parkedOthers = parkedKeys.some(k => k !== activeSeq);
  const noteChips = (
    <>
      {roNote && <span className="plm-ronote plm-dq-chip" role="status" title={roNote}>{roNote}</span>}
      {offerValidate && (
        <button type="button" className="ca-btn plm-back-btn plm-goval-btn" onClick={onGoToValidation}
          title="Open this batch in Validate mode">
          Go to Validation <i className="ti ti-arrow-right" aria-hidden="true" />
        </button>
      )}
      {parkedOthers && (
        <span className="plm-ronote plm-ronote--unsaved plm-dq-chip is-unsaved" role="status" data-testid="plm-unsaved-note"
          title={`Unsaved changes on ${unsavedCount} layer${unsavedCount === 1 ? '' : 's'} — Save writes them all.`}>
          ● Unsaved changes on {unsavedCount} layer{unsavedCount === 1 ? '' : 's'} — Save writes them all.
        </span>
      )}
    </>
  );

  const actionBar = (withNotes: boolean) => (
      <AppToolbar
        right={
          <span className="plm-toolbar-right">
            {withNotes && noteChips}
            {/* Prototype v8 puts Audit on the RIGHT, beside Save — it is an
                action on this record, not a status chip. Still the shared,
                config-gated <AuditTrailButton> (standing rule 11), so a form
                with audit switched off shows nothing. */}
            <AuditTrailButton formName={AUDIT_FORM} sourceId={activeSeq ?? undefined} />
            {validateMode ? (
              <>
                {/* Prototype v13 Validate mode: Reject (ghost) · Validate (primary).
                    Reject needs only a batch still pending validation; Validate
                    also needs every check point answered, and — while No
                    Self-Review is on — a validator who analysed none of it. */}
                <button className="ca-btn" onClick={() => { void doReject(); }} disabled={!valPending || busy}
                  title={valPending
                    ? `Return the batch to the analyst — reason required (${formatCombo(effectiveCombo('alt+b'))})`
                    : 'This batch is not pending validation'}>
                  <i className="ti ti-arrow-back-up" aria-hidden="true" /> Reject
                </button>
                <button className="ca-btn ca-btn-solid" onClick={() => { void doValidate(); }}
                  disabled={!!valDisabledWhy || busy}
                  title={valDisabledWhy ?? `Validate this batch (${formatCombo(effectiveCombo('alt+v'))})`}>
                  <i className="ti ti-circle-check" aria-hidden="true" /> Validate
                </button>
              </>
            ) : (<>
            {/* Prototype v8 emphasis: Save is a PLAIN GHOST button (white, ink
                text, grey border — identical to Back / Preview / Audit) and only
                Submit is the SOLID orange primary. In the prototype's toolbar
                Save is `class="ca ghost"` and Submit `class="ca"` — the earlier
                orange-OUTLINED Save (`plm-btn--save`) broke the uniform ghost row
                (owner report 2026-09-25 "i want like this"). */}
            <button
              className="ca-btn"
              onClick={() => { void doSave(); }}
              disabled={!activeSeq || !anyUnsaved || (readOnly && parkedKeys.length === 0)}
              title={`Save ${unsavedCount > 1 ? `the ${unsavedCount} changed layers` : 'this layer'} (${formatCombo(effectiveCombo('mod+s'))})`}
            >
              <span className="tb-i">💾</span> Save
            </button>
            {/* v13 (2026-09-28): the analyst of a re-read SUBMITS it explicitly —
                that is what lifts the blind and locks an Inter read — and may
                re-open it (reason required, audited; an Inter read is then
                marked "Not blind"). Only on their own re-read. */}
            {myReRead && !batchLocked && !viewMode && (myReRead.reReadSubmitted ? (
              <button className="ca-btn" onClick={() => { void doReopenReRead(); }} disabled={busy}
                title="Re-open your submitted read (reason required — audited)">
                <span className="tb-i">↺</span> Re-open Read
              </button>
            ) : (
              <button className="ca-btn" onClick={() => { void doSubmitReRead(); }} disabled={busy}
                title={myReRead.reReadKind === 'Inter'
                  ? 'Submit your blind Inter-dup read — the original becomes visible and your read locks'
                  : 'Submit your Intra-dup read'}>
                <span className="tb-i">✔</span> Submit Read
              </button>
            ))}
            <button
              className="ca-btn ca-btn-solid"
              onClick={() => { void doSubmit(); }}
              disabled={batchReadOnly || layers.length === 0 || busy}
              title="Check the batch and open its Data Package (Alt+U)"
            >
              <span className="tb-i">⬆</span> Submit
            </button>
            </>)}
          </span>
        }
        left={
          <>
            {/* Prototype v8 order: Back · Rollback · (Report · Preview from the
                shared toolbar) · ⋯ · Audit. The four occasional actions move
                behind the ⋯ so the bar reads as the workflow, not a list of
                everything the form can do — v7's review was "toolbar busy".
                Carry Over Results moved into the Sequence panel, where the
                layers it copies between live. */}
            {/* Outlined in the accent colour so it is easy to find (owner 2026-11-03,
                "back button is hard to view"); Submit stays the one SOLID button. */}
            <button className="ca-btn plm-back-btn" onClick={onBack}
              title={`Back to the QC batch (${formatCombo(effectiveCombo('esc'))})`}>
              <i className="ti ti-arrow-left" aria-hidden="true" /> Back
            </button>
            {/* Rollback — un-submit. Reset clears a batch still being ENTERED;
                once submitted the screen is read-only and Reset is disabled, so
                without this the only way back was the QC Batch list's Rollback
                (user request 2026-09-24). It is the SAME operation — see
                doRollback: the batch returns to Pending Submission, its Entered
                layers stay Entered, and the Data Package is reopened so its
                Submit shows again. Offered ONLY once submitted, which is exactly
                when Reset is not — the two never compete for the same state. Also in
                VIEW mode (owner request 2026-10-08): the batch list offers a
                submitted batch in View mode only, so that is the usual way to
                reach one, and the screen moves to Enter once it is rolled back. */}
            {/* Validate mode's Reject IS this rollback (with the Validate-mode
                wording), so the button steps aside rather than offering it twice. */}
            {!validateMode && <button
              className="ca-btn plm-tb-keep-icon"
              onClick={() => { void doRollback(); }}
              disabled={!batchLocked || busy}
              title={batchLocked
                ? `Un-submit this batch and reopen it for result entry `
                  + `(${formatCombo(effectiveCombo('alt+b'))})`
                : 'This batch has not been submitted yet'}
            >
              <i className="ti ti-arrow-back-up" aria-hidden="true" /> Rollback
            </button>}
          </>
        }
        // No New Component / Delete here: legacy drives BOTH the layer list and
        // the exam grid from small + / − pairs sitting directly above each grid,
        // not from the form toolbar (user request 2026-09-23).  The exam pair is
        // in PlmExamTab's bar; the layer pair is above the rail below.
        // Refresh on the bar like every other form (owner 2026-10-19: it was hidden in the ⋯ menu): re-reads the
        // batch status, the layers and the open layer - e.g. after another analyst submits or rolls back.
        onRefresh={refreshAll}
        refreshing={loading}
        onPreview={reportNames.length > 0 ? () => { void doPreview(); } : undefined}
        previewLabel="Preview"
        previewDisabled={!reportName || busy}
        beforePreview={reportNames.length > 0 ? (
          /* No "Report" caption — the prototype shows the picker alone, and the
             option text (PLM_Results) already names it. */
          <select
            className="plm-input plm-report-pick"
            value={reportName}
            onChange={e => setReportName(e.target.value)}
            aria-label="Report"
          >
            {reportNames.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        ) : undefined}
        beforeRefresh={
          <>
            <button
              className="ca-btn"
              onClick={() => setChartsOpen(true)}
              title={`PLM Statistical QC charts (${formatCombo(effectiveCombo('alt+c'))})`}
            >
              <i className="ti ti-chart-dots" aria-hidden="true" /> QC Charts
            </button>
            {/* Daily QC on the toolbar (owner 2026-10-19, G5 PlmV2DailyQc): opens
                the PLM Daily QC list. */}
            <button
              className="ca-btn"
              onClick={openDailyQc}
              title={`Open the PLM Daily QC list (${formatCombo(effectiveCombo('alt+k'))})`}
            >
              <i className="ti ti-microscope" aria-hidden="true" /> Daily QC
            </button>
            {moreMenu}
          </>
        }
      />
  );

  /**
   * The bars docked on TOP of the Sequence card, above its header strip — where
   * the prototype puts `#dailyBar` and `#interBar` (owner report 2026-09-29).
   */
  const sequenceTop = (
    <>
      {/* v13 Daily QC bar (prototype `renderDaily`): today's reference-slide
          check for the signed-in analyst — their layers stay locked until it
          passes or a supervisor overrides. Enter mode only. */}
      {dailyStatus && me && !batchLocked && !viewMode && (() => {
        // Optional (2026-10-17): no blocking state — an amber "optional for now" note.
        const b = dailyBar(dailyStatus, dailyEnforced);
        const open = dailyStatus.state === 'none' || dailyStatus.state === 'pending' || dailyStatus.state === 'fail';
        return (
          <div className={`plm-dailybar is-${b.tone}`} role="status">
            {/* One line, as G5 words it (owner 2026-10-19): "Daily QC · date — text". */}
            <div className="plm-dailybar__m">
              <b>Daily QC</b>{dailyStatus.date ? ` · ${fmtLocalDate(dailyStatus.date)}` : ''} — {b.text}
            </div>
            {open && dailyEnforced && (
              <div className="plm-dailybar__b">
                <button type="button" className="ca-btn ca-btn--sm ca-btn-solid"
                  onClick={() => setDailyOpen({
                    oid: dailyStatus.state === 'pending' ? dailyStatus.today?.oid ?? null : null,
                  })}>
                  {dailyStatus.state === 'pending' ? 'Read now' : dailyStatus.state === 'fail' ? 'Re-read' : 'Run Reference QC'}
                </button>
                {dailyStatus.state === 'fail' && dailyStatus.canOverride && (
                  <button type="button" className="ca-btn ca-btn--sm"
                    onClick={() => { void doDailyOverride(); }} disabled={busy}>Supervisor override</button>
                )}
              </div>
            )}
          </div>
        );
      })()}
      {/* The Inter-dup bar (owner rule 2026-10-07): the original analyst adds
          an Inter-dup on their own sample; it then waits here for a SECOND
          analyst, who starts it and reads it blind. Shown only to someone who
          may take one — the original analyst has nothing to do here. */}
      {interOptions.length > 0 && !batchLocked && !viewMode && (
        <div className="plm-interbar">
          <div className="plm-interbar__t">
            <b>Inter-dup</b> · first analyst {header?.firstAnalyst ?? '—'}
          </div>
          <div className="plm-interbar__m">
            An Inter-dup is waiting for a second analyst: press <b>Start Inter-dup</b> to read it{' '}
            <b>blind</b> — you won't see the original result until you submit.
          </div>
          <div className="plm-interbar__row">
            <select className="plm-input" value={interPick}
              onChange={e => setInterPick(e.target.value)} aria-label="Inter-dup to read">
              {interOptions.map(l => (
                <option key={l.sequenceOid} value={l.sequenceOid}>{dupLabel(l.sampleL)}</option>
              ))}
            </select>
            <button type="button" className="ca-btn ca-btn--sm ca-btn-solid"
              onClick={() => { void doStartInterdup(); }}
              disabled={busy || !interPick}>Start Inter-dup</button>
          </div>
        </div>
      )}
    </>
  );

  if (chartsOpen) {
    return <PlmQcChartsPage initialType={chartsType} onBack={() => { setChartsOpen(false); setChartsType('Inter'); }} />;
  }
  if (dailyOpen) {
    const closeDaily = () => {
      setDailyOpen(null);
      // A read may have passed (or been overridden) — re-read the gate,
      // and the passed reads the Daily QC ID picker offers.
      void fetchPlmDailyQcStatus().then(setDailyStatus);
      void fetchPlmDailyQcChoices().then(setDailyChoices).catch(() => undefined);
    };
    // The read opens IN PLACE (Run Reference QC / Read now / Re-read), so the shared return bar does
    // not appear on its own — the same "Back to PLM Result Entry" band is shown here (owner 2026-11-03).
    return (
      <div className="plm-daily-host">
        <ReturnBar inline label="PLM Result Entry" onBack={closeDaily}
          detail={header?.analyticalBatchId
            ? `Batch ${header.analyticalBatchId}${activeLayer?.sampleL ? ` · ${activeLayer.sampleL}` : ''}`
            : undefined} />
        <PlmDailyQcPage oid={dailyOpen.oid} onBack={closeDaily} />
      </div>
    );
  }

  return (
    <div className="plm-root">
      {/* FormTitle publishes the load-time + Audit chips to the toolbar
          (usePublishFormMeta) — it renders no heading of its own any more. */}
      {/* Prototype v8 puts BOTH chips in the top banner, right after the title
          — not on the action bar, which is for things you can press. So
          <FormTitle> is gone: its only job here was publishing the load time to
          the TOOLBAR, and leaving it in would render the chip twice. */}
      <PlmTopbarMeta
        modes={modeSwitch}
        loadMs={loadMs}
        loading={loading}
        dirty={dirty}
        onPrev={prevLayer}
        onNext={nextLayer}
      />

      {/* The workflow island docks in the APP's top bar, the slot SDMS and QC
          Batch already portal into (BO UI Standard §5) — not a second banner of
          our own. The prototype draws one banner because it is a standalone
          page; here that banner IS the app's top bar, which already carries the
          title and the user. */}
      <TopbarStepper steps={islandSteps} />

      {/* The read-only / unsaved notes are chips on the action bar below (noteChips). */}

      {/* G5's Retrieve bar: Select Mode · QC Batch ID · Job ID · Retrieve. */}
      {/* Hidden for now (owner request 2026-10-16) — PLM_RETRIEVE_BAR_ENABLED. */}
      {PLM_RETRIEVE_BAR_ENABLED && onRetrieve && <PlmRetrieveBar batchOid={batchOid} mode={mode} onRetrieve={onRetrieve} />}

      {actionBar(true)}

      {/* Prototype v8: Sample Information is a FULL-WIDTH card between the
          action bar and the split, NOT a block inside the right-hand column.
          It describes the LAYER, which both the sequence rail and the tabs are
          about, so it sits above both rather than beside one of them. The
          record-head strip that used to precede it is gone too — the app's top
          bar already carries the batch, the layer and the unsaved marker.
          Hidden on a BLIND layer (v13): its sample header is part of what is
          withheld. */}
      {!activeBlind && (
          // G5 header (PLMV2_Entry "SampleInfo", owner request 2026-10-16): FOUR
          // columns, 19 / 27 / 27 / 27 — Daily QC ID · QC Batch ID · Temperature |
          // SysSampleCode · Sample Name · Sample Location · Sample Notes | Analyzed
          // Date / By · Entered Date / By | PLM Scope · Stereo Microscope · Hot Plate ·
          // Thermohydrometer. Temperature came back on owner request 2026-10-28
          // ("PLM Analysis header is missing Temperature, please add it back"), in
          // G5's layout too. Dispersion Oil stays off the header (its stored value
          // is kept and still saved / carried over untouched).
          <div className="plm-context plm-context--g5 plm-hdr-std">
        <div className="plm-context__col">
          {/* Daily QC ID (G5 parity 2026-10-15): a PASSED read, the layer
              analyst's own; required while the Daily QC gate is on. */}
          <div className="plm-context__item">
            <label>Daily QC ID{dailyRequired && <span className="plm-req" aria-hidden="true">*</span>}</label>
            {draft ? (
              <PlmDailyQcPicker
                value={draft.dailyQcId}
                choices={dailyChoices}
                disabled={readOnly}
                required={dailyRequired}
                onChange={id => {
                  setDraft(d => (d ? { ...d, dailyQcId: id } : d)); setDirty(true);
                  // G5: told at once (Save refuses it as well).
                  const read = dailyChoices.find(c => c.dailyQcId === id);
                  const analyst = draft.analyzedByOid ?? me;
                  if (read && analyst && read.analystOid.toLowerCase() !== analyst.toLowerCase())
                    notify.warning(`Daily QC ${read.dailyQcId} was performed by ${read.analystName ?? 'another analyst'}. `
                      + `Pick the Daily QC performed by ${nameOf(analyst) ?? 'the analyst'} (Analyzed By).`);
                }}
              />
            ) : <span>—</span>}
          </div>
          <div className="plm-context__item">
            <label>QC Batch ID</label>
            <span data-testid="plm-qcbatch-id">{header?.analyticalBatchId ?? ''}</span>
          </div>
          {/* The room temperature the layer was read at (°C), free text as stored
              (dbo.SamplePLM Temperature, NVARCHAR) — back on the header 2026-10-28. */}
          <div className="plm-context__item">
            <label htmlFor="plm-temperature">Temperature &deg;C</label>
            {draft ? (
              <input id="plm-temperature" className="plm-input" aria-label="Temperature"
                value={draft.temperature ?? ''} disabled={readOnly}
                onChange={e => { setDraft({ ...draft, temperature: e.target.value || null }); setDirty(true); }} />
            ) : <span>—</span>}
          </div>
        </div>

        <div className="plm-context__col">
          {/* Prototype v8 replaces "Sample #" with SysSampleCode — the LAYER
              id. It is the identifier the sequence, the QC pairing and the
              report all key on; the bare sample number ("1") identifies
              nothing on its own once re-reads are in the list. */}
          <div className="plm-context__item">
            <label>SysSampleCode</label>
            <span data-testid="plm-syscode">{activeLayer?.sampleL ?? ''}</span>
          </div>
          {/* Sample Name on a normal layer; on a QC re-read the prototype
              swaps this slot to QC Type, so the header follows the row the
              analyst selected instead of showing a field that does not apply. */}
          {activeLayer?.qcType && activeLayer.qcType !== 'Sample' ? (
            <div className="plm-context__item">
              <label>QC Type</label>
              <span>{dupLabel(activeLayer.qcType)}</span>
            </div>
          ) : (
            <div className="plm-context__item">
              <label>Sample Name</label>
              <span>{context?.clientSampleId ?? ''}</span>
            </div>
          )}
          {/* Prototype v8 QC-mode swap: a re-read shows the layer it re-reads
              (its Source Layer) rather than a sample location it does not own. */}
          {activeIsReRead ? (
            <div className="plm-context__item">
              <label>Source Layer</label>
              <span>{sourceLayerId ?? '—'}</span>
            </div>
          ) : (
            <div className="plm-context__item">
              <label>Sample Location</label>
              <span>{context?.sampleLocation ?? ''}</span>
            </div>
          )}
          {/* Prototype v8 QC-mode swap: a dup shows the Reference ID (the
              original layer it is compared against) with a Review link that opens
              the QC Result View, in place of the Sample Notes editor. */}
          {activeIsReRead ? (
            <div className="plm-context__item">
              <label>Reference ID</label>
              <span className="plm-refcell">
                <span className="plm-refcell__id">{sourceLayerId ?? '—'}</span>
                <button
                  type="button"
                  className="plm-review-link"
                  onClick={() => setTab('qc')}
                  title="Open the QC comparison for this dup"
                >Review</button>
              </span>
            </div>
          ) : (
            <div className="plm-context__item">
              <label>Sample Notes</label>
              {draft ? (
                <ComboSelect
                  value={draft.sampleNoteOid ?? ''}
                  options={lookups.sampleNotes.map(n => ({ value: n.id, label: n.name ?? '' }))}
                  onChange={v => { setDraft({ ...draft, sampleNoteOid: v || null }); setDirty(true); }}
                  placeholder=""
                  disabled={readOnly}
                />
              ) : <span>—</span>}
            </div>
          )}
        </div>

        {/* WHO analysed the layer and WHEN (owner feedback 2026-10-07). Analyzed
            Date and Analyzed By are stamped by Save — the date and time of every
            save, the signed-in analyst on the first — so they are shown, not
            picked; the name opens for editing only once the layer is entered.
            Entered Date is the sample's Received date and, with Entered By,
            always read-only. Date AND time, 24-hour (the shared helper). */}
        <div className="plm-context__col">
          <div className="plm-context__item">
            <label>Analyzed Date{!autoStamp && <span className="plm-req" aria-hidden="true">*</span>}</label>
            {!draft ? <span>—</span> : autoStamp ? (
              <span data-testid="plm-analyzed-date"
                title="Stamped by Save — the date and time of the last save">
                {shownAnalyzedDate ? fmtLocalDateTime(shownAnalyzedDate) : ''}
              </span>
            ) : (
              <input
                type="date"
                className={`plm-input${draft.analyzedDate ? '' : ' is-reqempty'}`}
                /* <input type="date"> speaks ISO yyyy-MM-dd; slice rather than
                   parse so a Central wall-clock value is never shifted a day by
                   a UTC round-trip (§14b.6 kill-switch). */
                value={(draft.analyzedDate ?? '').slice(0, 10)}
                disabled={readOnly}
                onChange={e => {
                  setDraft({ ...draft, analyzedDate: e.target.value ? `${e.target.value}T00:00:00` : null });
                  setDirty(true);
                }}
              />
            )}
          </div>
          <div className="plm-context__item">
            <label>Analyzed By{!autoStamp && <span className="plm-req" aria-hidden="true">*</span>}</label>
            {!draft ? <span>—</span> : analystEditable ? (
              <ComboSelect
                value={draft.analyzedByOid ?? ''}
                options={analystOptions}
                onChange={v => { setDraft({ ...draft, analyzedByOid: v || null }); setDirty(true); }}
                placeholder=""
                disabled={readOnly}
              />
            ) : (
              <span data-testid="plm-analyzed-by"
                title="Filled in by the first Save — the signed-in analyst">
                {shownAnalystName ?? ''}
              </span>
            )}
          </div>
          <div className="plm-context__item">
            <label>Entered Date</label>
            <span data-testid="plm-entered-date" title="The sample's Received date">
              {enteredDateShown ? fmtLocalDateTime(enteredDateShown) : ''}
            </span>
          </div>
          <div className="plm-context__item">
            <label>Entered By</label>
            {/* A layer with no observation yet (an Inter-dup / Intra-dup added after
                the batch was made) shows the analyst who reads it — the one the
                first Save stamps (owner 2026-10-19, G5 parity). */}
            <span data-testid="plm-entered-by">
              {context?.enteredByName || (previewStamp ? nameOf(me) ?? '' : '')}
            </span>
          </div>
        </div>

        <div className="plm-context__col">
          {/* The four devices (G5 parity 2026-10-15): with PLM Instrument Setup
              rows, each is a drop-down of those rows (Station first) and a pick
              fills the other three and the station; without, a plain box. */}
          {([
            ['microscopeId', 'PLM Scope', true],
            ['microscope', 'Stereo Microscope', false],
            ['hotPlate', 'Hot Plate', false],
            ['thermohydrometer', 'Thermohydrometer', false],
          ] as const).map(([field, label, req]) => (
            <div className="plm-context__item" key={field}>
              <label>{label}{req && <span className="plm-req" aria-hidden="true">*</span>}</label>
              {!draft ? <span>—</span> : stationRows.length > 0 ? (
                <PlmDeviceCombo
                  field={field}
                  draft={draft}
                  rows={stationRows}
                  disabled={readOnly}
                  required={req}
                  onChange={next => { setDraft(next); setDirty(true); }}
                />
              ) : (
                // No setup rows: the names remembered from earlier saves (G5 parity
                // 2026-10-16) — pick-only, no free text (owner 2026-10-19, G5 parity).
                <PlmDevicePick
                  id={`plm-dev-${field}`}
                  className={req && !draft[field]?.trim() ? 'is-reqempty' : undefined}
                  ariaLabel={label}
                  names={deviceNames(field, utilities)}
                  value={draft[field]}
                  disabled={readOnly}
                  onChange={v => { setDraft({ ...draft, [field]: v }); setDirty(true); }} />
              )}
            </div>
          ))}
        </div>
      </div>
      )}

      <div className="plm-body">
        {/* ── layer list ───────────────────────────────────────────────── */}
        <aside className="plm-layers">
          {/* Caption + live count + search (mockup, 2026-09-24). Neutral
              styling per the BO UI Standard — see sectionPanel.css. */}
          {/* Prototype v8 `.pane-hd`: "Sequence N of M" with Add Layer / Remove
              in the header strip itself. "N of M" — the position of the layer
              you are on within the sequence — is what an analyst working down
              a batch actually wants to know; a bare total is not. */}
          <SectionPanel
            title={layerIdx >= 0
              ? `Sequence ${layerIdx + 1} of ${shownLayers.length}`
              : `Sequence (${shownLayers.length})`}
            icon="ti-list-details"
            className="plm-layers-sec"
            top={sequenceTop}
            headerRight={
              <>
                {/* View and Validate never change the layer list, so the pair is
                    not offered there at all — the prototype v13 hides `#addLayer`
                    in both modes (renderLayers). Disabled-but-visible also pushed
                    Remove out of the card beside a long status chip (owner report
                    2026-09-29, "Pending SDMS Validation"). */}
                {!viewMode && (
                  <>
                    {/* How many layers Add Layer adds (user feedback C6, 2026-11-02). Not an edit
                        of the layer, so the unsaved-changes guard ignores it. */}
                    <input
                      type="number"
                      min={1}
                      max={MAX_LAYERS_AT_ONCE}
                      className="plm-input plm-addlayer-count"
                      aria-label="Layers to add"
                      data-unsaved-ignore
                      disabled={readOnly || !activeSeq || busy}
                      value={addLayerCount}
                      title="How many layers to add. Separate materials (drywall, texture, joint compound) need separate layers."
                      onChange={e => setAddLayerCount(e.target.value)}
                      onKeyDown={e => {
                        if (e.key !== 'Enter') return;
                        // Stopped here: the same keypress would otherwise reach the window
                        // listener of the confirm it opens and answer it unseen.
                        e.preventDefault();
                        e.stopPropagation();
                        void doAddLayer();
                      }}
                    />
                    <button
                      type="button"
                      className="ca-btn ca-btn--sm"
                      onClick={() => { void doAddLayer(); }}
                      disabled={readOnly || !activeSeq || busy}
                      title={`Add the number of layers in the box to this sample (${formatCombo(effectiveCombo('alt+l'))})`}
                    ><span className="tb-i tb-i-green" aria-hidden="true">＋</span> Add Layer</button>
                    <button
                      type="button"
                      className="ca-btn ca-btn--sm"
                      onClick={() => { void doRemoveLayer(); }}
                      disabled={(readOnly && !canRemoveDup) || !activeSeq || busy}
                      title="Remove the selected layer"
                    ><span className="tb-i tb-i-red" aria-hidden="true">🗑</span> Remove</button>
                  </>
                )}
              </>
            }
          >
          {/* v13 `#batchChip`: where the batch is in the on-demand re-read flow
              (In Analysis → Pending Interdup → Interdup In Progress → Ready to
              Submit), then its own status once submitted. Its own line, LEFT,
              under the title — in the header it sat between the title and Add
              Layer / Remove and pushed the pair onto a second row (owner
              screenshot 2026-09-29: buttons up beside the title, chip left). */}
          {header && (
            <div className="plm-seq-stage">
              <span className={`plm-stagechip is-${batchLocked ? 'ok' : stageTone(header.plmStage)}`}
                title="Batch status">
                {batchLocked ? (header.statusText ?? 'Submitted') : (header.plmStage ?? 'In Analysis')}
              </span>
              {/* Add QC (user feedback C8, 2026-11-02). On this row because the header strip above
                  has no room for a third button beside Add Layer / Remove (see the note above). */}
              {!viewMode && (
                <button
                  ref={addQcBtn}
                  type="button"
                  className="ca-btn ca-btn--sm plm-seq-stage__act"
                  onClick={() => openAddQc(activeLayer)}
                  disabled={batchReadOnly || !activeLayer || busy || !!addQcBlock}
                  aria-haspopup="menu"
                  title={addQcBlock
                    ?? `Add an Intra-dup or Inter-dup for this layer (${formatCombo(effectiveCombo('alt+q'))})`}
                ><i className="ti ti-copy-plus" aria-hidden="true" /> Add QC ▾</button>
              )}
            </div>
          )}
          <AppDataGrid
            rows={shownLayers}
            columns={layerColumns}
            getRowId={r => r.sequenceOid}
            selectedRowId={activeSeq}
            onRowClick={r => { void selectLayer(r.sequenceOid); }}
            /* A tick box before the SampleL# column, as legacy shows on its own
               layer rail.  PLM works ONE layer at a time — the tabs, the header
               strip and the exam grid all describe the active layer — so the box
               mirrors that single active row rather than opening multi-select:
               ticking a row makes it active (the same as clicking it), and
               unticking is a no-op because some layer is always current. */
            /* NO fixedLayout — deliberately. It made the table exactly the rail's
               330px and therefore ellipsis-clipped the SysSampleCode to
               "26092300…", hiding which layer each row is. The prototype's rail
               is the same 330px but uses an AUTO-layout table whose cells are
               `white-space:nowrap`, so the code always shows in full and the
               pane scrolls sideways when it has to — the code is the row's
               identity, the sideways scroll is the cheaper trade (owner
               report 2026-09-25, side-by-side). The Status pill no longer
               pushes width either: it reads "Pending Entry", not "Pending
               Result Entry" (see plmStageLabel). */
            selectable
            selectedIds={new Set(activeSeq ? [activeSeq] : [])}
            onSelectionChange={next => {
              // The grid's id type is string | number; PLM keys on sequenceOid,
              // which is always a string.
              const picked = [...next].find(id => id !== activeSeq);
              if (typeof picked === 'string') void selectLayer(picked);
            }}
            loading={loading}
            defaultPageSize={20}
            emptyText="This batch has no sample layers."
          />
          {/* Carry Over Results lives HERE, under the sequence it copies
              between — not on the form toolbar (prototype v8, `.lfilt`).  The
              toolbar describes the batch; this describes the layer list.  Same
              handler and the same Alt+Y binding as before. */}
          <div className="plm-seq-foot">
            <button
              type="button"
              className="ca-btn"
              onClick={() => setCarryOpen(true)}
              disabled={readOnly || layers.length < 2 || !activeSeq || busy || activeIsReRead}
              title={activeIsReRead
                ? 'A QC re-read must be an independent read — it cannot be copied from another layer'
                : layers.length < 2
                  ? 'There is only one layer to copy between'
                  : `Copy this layer's results onto other layers `
                    + `(${formatCombo(effectiveCombo('alt+y'))})`}
            >
              <i className="ti ti-copy" aria-hidden="true" /> Carry Over Results
            </button>
            {/* Dups are added ON DEMAND, after analysis, and slot in under the
                layer they re-read (owner rule 2026-10-07). */}
            <p className="plm-seq-hint">
              Dups are added <b>after analysis</b>, by the analyst who read the
              sample: use <b>Add QC</b> above (or <b>right-click</b> a sample row) for{' '}
              <b>Add Intra-dup</b> — your own second read — or{' '}
              {dupSteps ? (<>
                <b>Add Inter-dup</b>, which a second analyst then starts and reads
                blind. An Inter-dup stays hidden from others until it is submitted.
              </>) : (<>
                <b>Add Inter-dup</b>, which any other analyst reads blind — the first
                to save it. The sample stays hidden from everyone but its analyst
                until the batch is submitted.
              </>)}
            </p>
          </div>
          </SectionPanel>
        </aside>

        {/* ── sample context + tabs ────────────────────────────────────── */}
        {activeBlind && activeLayer ? (
          /* v13 blind card: the analyst holds a PENDING Inter re-read of this
             layer's group, so the original (and its Intra) is withheld until
             they submit — the server refuses the layer too (403 layer-blind). */
          <main className="plm-main is-blind">
            <div className="plm-blind" role="status">
              <div className="plm-blind__icon" aria-hidden="true">🔒</div>
              <div className="plm-blind__t">Blind analysis</div>
              {dupSteps ? (
                <p>
                  <b>{activeLayer.sampleL}</b> is hidden while you read its Inter-dup.
                  It becomes visible once you submit your read.
                </p>
              ) : (
                <p>
                  <b>{activeLayer.sampleL}</b> has an Inter-dup, so it is hidden from everyone
                  but its original analyst until the batch is submitted.
                </p>
              )}
              {(() => {
                const rr = blindingReRead(activeLayer, layers, me, dupFlow);
                return rr ? (
                  <button type="button" className="ca-btn ca-btn-solid"
                    onClick={() => { void selectLayer(rr.sequenceOid); }}>
                    {dupSteps || ownerOf(rr) ? 'Open my read' : 'Open the Inter-dup'} {dupLabel(rr.sampleL)}
                  </button>
                ) : null;
              })()}
            </div>
          </main>
        ) : (
        <main className="plm-main">
          {/* Prototype v8 tab strip: plain captions, a count pill where there
              is a count, orange active underline. No icons and no stacked
              sub-line — that made the strip two lines tall and pushed the
              working area down. */}
          <div className="plm-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={tab === 'stereo'}
              className={`plm-tab${tab === 'stereo' ? ' is-active' : ''}`}
              onClick={() => setTab('stereo')}
              title={stereoSummary(draft)}
            >
              Stereoscopic Observation
            </button>
            <button
              role="tab"
              aria-selected={tab === 'exam'}
              className={`plm-tab${tab === 'exam' ? ' is-active' : ''}`}
              onClick={() => setTab('exam')}
              title={examSummary(exams)}
            >
              PLM Exam
              {/* Blank seed rows are not components, so they are not counted —
                  a count of "1" on an untouched layer was the 2026-09-23 bug. */}
              {realExamCount > 0 && (
                <span className={`plm-tab__count${
                  exams.length && !examBalances(exams) ? ' is-warn' : ''}`}>
                  {realExamCount}
                </span>
              )}
            </button>
            <button
              role="tab"
              aria-selected={tab === 'qc'}
              className={`plm-tab${tab === 'qc' ? ' is-active' : ''}`}
              onClick={() => setTab('qc')}
              title={qcSummary}
            >
              QC Result View
              {qcCount > 0 && (
                <span className={`plm-tab__count${qcFailing > 0 ? ' is-warn' : ''}`}>
                  {qcCount}
                </span>
              )}
            </button>
            <button
              role="tab"
              aria-selected={tab === 'final'}
              className={`plm-tab${tab === 'final' ? ' is-active' : ''}`}
              onClick={() => setTab('final')}
              title="Read the whole layer back before submitting"
            >
              Final Confirmation
            </button>
            {validateMode && (
              <button
                role="tab"
                aria-selected={tab === 'checkpoints'}
                className={`plm-tab${tab === 'checkpoints' ? ' is-active' : ''}`}
                onClick={() => setTab('checkpoints')}
                title="The data package's check points — answered before Validate"
              >
                Check Points
                {checkPoints.length > 0 && (
                  <span className={`plm-tab__count${cpUnanswered > 0 ? ' is-warn' : ''}`}>
                    {cpUnanswered > 0 ? cpUnanswered : checkPoints.length}
                  </span>
                )}
              </button>
            )}
          </div>

          <div className="plm-tabpanel">
            {/* The cross-tab conflict warning (prototype v8): a persistent
                amber banner on the Stereoscopic and PLM Exam tabs whenever the
                two disagree, on top of the save-time confirm. */}
            {(tab === 'stereo' || tab === 'exam') && conflictMessage && (
              <div className="plm-conflict" role="alert">⚠ {conflictMessage}</div>
            )}
            {/* QC Result View is the ONE batch-wide tab, so it renders even
                with no layer selected — "select a layer" would be a false
                instruction for a view that is about the whole batch. */}
            {tab === 'checkpoints' ? (
              <PlmCheckPointsTab
                rows={checkPoints}
                loading={cpLoading}
                edits={cpEdits}
                onEdit={onCheckPointEdit}
                readOnly={!valPending || busy}
              />
            ) : tab === 'qc' ? (
              <PlmQcTab
                rows={qcRows}
                loading={qcLoading}
                activeSequenceOid={activeSeq}
                onOpenLayer={seq => { void selectLayer(seq); }}
                corrective={corrective}
                // Recorded until the batch is validated (the server refuses after).
                onSaveCorrective={(header?.status ?? 0) >= 4 ? undefined : saveCorrective}
                history={qcHistory}
                onOpenChart={kind => { setChartsType(kind); setChartsOpen(true); }}
              />
            ) : !draft ? (
              <div className="plm-exam__empty">Select a sample layer to begin.</div>
            ) : tab === 'stereo' ? (
              <PlmStereoTab
                value={draft}
                onChange={next => { setDraft(next); setDirty(true); }}
                materials={lookups.materials}
                readOnly={readOnly}
              />
            ) : tab === 'final' ? (
              <PlmFinalTab
                obs={draft}
                exams={exams}
                fiberTypeNames={exams.map(e =>
                  lookups.fiberTypes.find(t => t.id === e.fiberTypeOid)?.name ?? null)}
                materialNames={materialNamesForDraft}
                pointCountType={pointCount.pointCountType}
                pointCountLabel={pointCountLabel}
              />
            ) : (
              <PlmExamTab
                exams={exams}
                onChange={next => { setExams(next); setDirty(true); }}
                activeIndex={examIndex}
                onActiveIndexChange={setExamIndex}
                lookups={lookups}
                fiberTypeValues={fiberTypeValues}
                readOnly={readOnly || !!draft?.positiveStop}
                oilNames={utilities.oil}
                toolbarExtra={draft && (
                  <PlmContamControl
                    checked={draft.contaminationCheck}
                    state={contam}
                    disabled={readOnly}
                    onChange={on => { setDraft({ ...draft, contaminationCheck: on }); setDirty(true); }}
                  />
                )}
                pointCountType={pointCount.pointCountType}
                onPointCountTypeChange={next => { void onPointCountTypeChange(next); }}
                onOpenPointCount={() => {
                  // Legacy's two PCEntry_Execute guards (PLMViewController :121-168),
                  // verbatim wording. Without them PC Entry opened a grid with no
                  // columns, or one built for no type, and the analyst had to work
                  // out why it was empty.
                  if (!pointCount.pointCountType) {
                    notify.warning('Select the Point Count Type');
                    return;
                  }
                  // asbestosFiberNames IS legacy's own selection — Asbestos Fibers
                  // rows that HAVE a name, in Sort order (PCEntry_Execute :125). A
                  // blank row is a component the analyst has not identified yet, so
                  // the count has nothing to tally against it.
                  if (asbestosFiberNames.length === 0) {
                    notify.warning('Asbestos Fibers entry not found.');
                    return;
                  }
                  setPcOpen(true);
                }}
                onAddExam={addExam}
                onRemoveExam={() => { void removeExam(); }}
                onAddExamAt={addExamAt}
                onRemoveExamAt={(i) => { void removeExamAt(i); }}
                onMoveExam={moveExam}
                pointCountDisabled={pcDisabled || busy}
                pointCountFiberNames={pointCountFiberNames}
              />
            )}
          </div>
        </main>
        )}
      </div>

      <div className="plm-actbar-bottom">{actionBar(false)}</div>

      {commentOpen && (
        <PlmCommentDialog
          open={commentOpen}
          batchId={header?.analyticalBatchId ?? null}
          onCancel={() => setCommentOpen(false)}
          onSave={doComment}
        />
      )}

      {carryOpen && (
        <PlmCarryOverDialog
          open={carryOpen}
          layers={layers}
          currentSequenceOid={activeSeq}
          isLocked={l => !!(lockReason(l, layers, me, undefined, dupFlow) ?? dailyLockReason(l, me, dailyOk))}
          isBlind={l => dupFlow.simple && isBlind(l, layers, me, dupFlow)}
          onCancel={() => setCarryOpen(false)}
          onCopy={doCopy}
        />
      )}

      {/* Rollback (un-submit) — the SHARED modal the SDMS sheet and the QC Batch
          list already use, so the reason prompt, its guards ("Reported" samples
          block it) and its audit trail are identical wherever it is run from. */}
      {dupMenu && (
        <PlmRowMenu
          position={{ x: dupMenu.x, y: dupMenu.y }}
          label={`Dups for ${dupMenu.row.sampleL ?? 'this layer'}`}
          heading={dupMenu.row.sampleL ?? undefined}
          items={[
            { id: 'intra', label: 'Add Intra-dup', icon: 'ti-copy-plus',
              disabledReason: dupMenuHint(dupMenu.row, layers, me, 'Intra', batchReadOnly),
              onSelect: () => { void doAddDup(dupMenu.row, 'Intra'); } },
            { id: 'inter', label: 'Add Inter-dup', icon: 'ti-users-plus',
              disabledReason: dupMenuHint(dupMenu.row, layers, me, 'Inter', batchReadOnly),
              onSelect: () => { void doAddDup(dupMenu.row, 'Inter'); } },
            // Prototype v18: an OPEN Inter-dup is claimed from the same menu —
            // the Inter-dup bar's Start Inter-dup, on the right-clicked row.
            // Not in the SIMPLE flow: there the first save of the Inter-dup claims it.
            ...(dupSteps && isUnclaimedInter(dupMenu.row) ? [{
              id: 'claim', label: 'Claim Inter-dup', icon: 'ti-hand-grab',
              disabledReason: claimDupHint(dupMenu.row, layers, me, batchReadOnly),
              onSelect: () => { void doStartInterdup(dupMenu.row); },
            }] : []),
            { id: 'remove', label: dupMenu.row.reReadKind ? `Remove ${dupMenu.row.reReadKind}-dup` : 'Remove dup',
              icon: 'ti-x', separator: true, danger: true,
              disabledReason: removeDupHint(dupMenu.row, layers, me, batchReadOnly, dupFlow),
              onSelect: () => { void doRemoveLayer(dupMenu.row); } },
          ]}
          onClose={() => setDupMenu(null)}
        />
      )}

      {/* Submit's real destination: the SHARED package review popup (rule 8 — the
          same component SDMS Result Entry and the QC Batch list open). The analyst
          answers the check points here and presses Submit, which is what advances
          the batch to Pending Validation. */}
      {pkgOpen && header && (
        <SdmsPackageDetail
          windowed        /* legacy TargetWindow.NewModalWindow */
          fromSdms        /* opened from an entry screen — hide the redundant SDMS button */
          plmBatchOid={batchOid}  /* → the PLM Results tab instead of the SDMS categories */
          batch={{
            id: batchOid,
            batchId: header.analyticalBatchId,
            test: header.test,
            method: header.method,
            matrix: header.matrix,
            templateID: header.templateId,   /* NOTE the capital ID — SdmsPackageRow's spelling */
            templateName: null,
            jobIds: null,
            statusText: header.statusText ?? '',
          } as unknown as SdmsPackageRow}
          approval={false}
          /* Submit is a BATCH action, so it keys on the batch lock only — never
             on the layer that happens to be open. `readOnly` also carries the
             per-layer ownership lock, so opening Submit while another
             analyst's layer (e.g. their Inter-dup) was on screen made the whole
             package read-only (user report 2026-10-16). */
          canWrite={!batchReadOnly}
          packageStatus={pkgStatus}
          packageCreatedBy={pkgCreated.by}
          packageCreatedDate={pkgCreated.date}
          /* One entry — this batch — so the package view's pickers name it rather
             than showing nothing; PLM has no batch list behind this screen. */
          allBatches={[{
            id: batchOid, batchId: header.analyticalBatchId, test: header.test,
            method: header.method, matrix: header.matrix,
            templateId: header.templateId, templateName: null, jobIds: null,
            statusText: header.statusText ?? '',
          }]}
          onClose={() => { setPkgOpen(false); void loadShell(); }}
          onRefresh={() => { /* entry screen has no list to reload */ }}
          onValidated={() => { /* validation happens on the Data Review forms */ }}
          onSubmitted={() => {
            // The popup advanced the batch (Pending Submission → Pending Validation).
            // Re-read the header so this screen drops to read-only, exactly as a
            // submitted batch should — `editable` is Status < 2.
            setPkgOpen(false);
            void loadShell();
          }}
          onSubmitSuccess={advanced => { void afterSubmit(advanced); }}
        />
      )}

      {/* PC Entry — keyed on the type so switching 400 ⇄ 1000 rebuilds the grid
          at the right number of slides rather than keeping stale rows. */}
      {pcOpen && pointCount.pointCountType && (
        <PlmPointCountDialog
          key={`${activeSeq}-${pointCount.pointCountType}`}
          open={pcOpen}
          fiberNames={asbestosFiberNames}
          pointCountType={pointCount.pointCountType}
          existing={pointCount.slides}
          layerId={activeLayer?.sampleL ?? null}
          // Cancel just closes (prototype `closePc`): the Point Count checkbox
          // and Type stay as the analyst set them, whether or not a count was
          // stored — the box is an in-memory switch, the count is what is saved.
          onCancel={() => setPcOpen(false)}
          onSave={onSavePointCount}
        />
      )}
    </div>
  );
}
